import { assign, fromPromise, setup } from 'xstate';
import type { ProductRole, RecordingLifecycle } from '@academy/domain';

export const errorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim().length > 0) return error.message;
  if (typeof error === 'string' && error.trim().length > 0) return error;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = Reflect.get(error, 'message');
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return 'An unexpected request error occurred.';
};

export type BootstrapData = {
  accountId: number;
  roles: readonly ProductRole[];
  email?: string | undefined;
  displayName?: string | undefined;
  sessionSubjectHash?: string | undefined;
  sessionGenerationHash?: string | undefined;
};

type BootstrapInput = {
  load: (signal: AbortSignal) => Promise<BootstrapData | null>;
};

type ApplicationContext = {
  loader: BootstrapInput['load'];
  session: BootstrapData | null;
  error: string | null;
};

export const applicationMachine = setup({
  types: {
    context: {} as ApplicationContext,
    events: {} as { type: 'RETRY' } | { type: 'SIGN_OUT' },
    input: {} as BootstrapInput,
  },
  actors: {
    loadSession: fromPromise(async ({ input }: { input: BootstrapInput['load'] }) =>
      input(new AbortController().signal),
    ),
  },
  actions: {
    clearSession: assign({ session: null }),
  },
}).createMachine({
  id: 'application',
  initial: 'booting',
  context: ({ input }) => ({ loader: input.load, session: null, error: null }),
  states: {
    booting: {
      invoke: {
        src: 'loadSession',
        input: ({ context }: { context: ApplicationContext }) => context.loader,
        onDone: [
          {
            guard: ({ event }) => event.output === null,
            target: 'unauthenticated',
          },
          {
            target: 'authenticated',
            actions: assign({ session: ({ event }) => event.output }),
          },
        ],
        onError: {
          target: 'failed',
          actions: assign({ error: ({ event }) => errorMessage(event.error) }),
        },
      },
    },
    unauthenticated: {},
    authenticated: {
      on: { SIGN_OUT: { target: 'unauthenticated', actions: 'clearSession' } },
    },
    failed: { on: { RETRY: 'booting' } },
  },
});

export const createQueryMachine = <TData>() =>
  setup({
    types: {
      context: {} as {
        load: (signal: AbortSignal) => Promise<TData>;
        isEmpty: (data: TData) => boolean;
        data: TData | null;
        error: string | null;
      },
      events: {} as { type: 'RETRY' } | { type: 'REFRESH' },
      input: {} as {
        load: (signal: AbortSignal) => Promise<TData>;
        isEmpty?: ((data: TData) => boolean) | undefined;
      },
    },
    actors: {
      query: fromPromise(
        async ({
          input,
          signal,
        }: {
          input: (signal: AbortSignal) => Promise<TData>;
          signal: AbortSignal;
        }) => input(signal),
      ),
    },
  }).createMachine({
    id: 'query',
    initial: 'loading',
    context: ({ input }) => ({
      load: input.load,
      isEmpty: input.isEmpty ?? (() => false),
      data: null,
      error: null,
    }),
    states: {
      loading: {
        invoke: {
          src: 'query',
          input: ({
            context,
          }: {
            context: {
              load: (signal: AbortSignal) => Promise<TData>;
            };
          }) => context.load,
          onDone: [
            {
              guard: ({ context, event }) => context.isEmpty(event.output),
              target: 'empty',
              actions: assign({ data: ({ event }) => event.output, error: null }),
            },
            {
              target: 'ready',
              actions: assign({ data: ({ event }) => event.output, error: null }),
            },
          ],
          onError: {
            target: 'failed',
            actions: assign({ error: ({ event }) => errorMessage(event.error) }),
          },
        },
      },
      refreshing: {
        invoke: {
          src: 'query',
          input: ({
            context,
          }: {
            context: {
              load: (signal: AbortSignal) => Promise<TData>;
            };
          }) => context.load,
          onDone: [
            {
              guard: ({ context, event }) => context.isEmpty(event.output),
              target: 'empty',
              actions: assign({ data: ({ event }) => event.output, error: null }),
            },
            {
              target: 'ready',
              actions: assign({ data: ({ event }) => event.output, error: null }),
            },
          ],
          onError: [
            {
              guard: ({ context }) => context.data !== null && context.isEmpty(context.data),
              target: 'empty',
              actions: assign({ error: ({ event }) => errorMessage(event.error) }),
            },
            {
              target: 'ready',
              actions: assign({ error: ({ event }) => errorMessage(event.error) }),
            },
          ],
        },
      },
      ready: { on: { REFRESH: 'refreshing' } },
      empty: { on: { REFRESH: 'refreshing' } },
      failed: { on: { RETRY: 'loading', REFRESH: 'loading' } },
    },
  });

type CatalogEntryEvent =
  | { type: 'CREATE' }
  | { type: 'UPDATE' }
  | { type: 'DEACTIVATE' }
  | { type: 'SUCCEEDED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'RETRY' }
  | { type: 'EDIT' };

export const catalogEntryMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      operation: 'CREATE' | 'UPDATE' | 'DEACTIVATE' | null;
      error: string | null;
    },
    events: {} as CatalogEntryEvent,
  },
}).createMachine({
  id: 'catalogEntry',
  initial: 'editing',
  context: { revision: 0, serverRevision: null, operation: null, error: null },
  states: {
    editing: {
      on: {
        CREATE: {
          target: 'saving',
          actions: assign({ operation: 'CREATE', error: null }),
        },
        UPDATE: {
          target: 'saving',
          actions: assign({ operation: 'UPDATE', error: null }),
        },
        DEACTIVATE: {
          target: 'saving',
          actions: assign({ operation: 'DEACTIVATE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'saved',
          actions: assign({
            revision: ({ event }) => event.revision,
            serverRevision: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: { on: { EDIT: 'editing' } },
    conflict: { on: { EDIT: 'editing' } },
    failed: { on: { RETRY: 'saving', EDIT: 'editing' } },
  },
});

type CurriculumPublicationEvent =
  | { type: 'EDIT' }
  | { type: 'VALIDATE' }
  | { type: 'VALIDATION_PASSED'; warnings: number }
  | { type: 'VALIDATION_FAILED'; issues: number }
  | { type: 'PUBLISH' }
  | { type: 'PUBLISHED'; revision: number }
  | { type: 'BRANCH' }
  | { type: 'BRANCHED'; planId: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'RETRY' };

export const curriculumPublicationMachine = setup({
  types: {
    context: {} as {
      planId: number | null;
      revision: number;
      validationIssueCount: number;
      validationWarningCount: number;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as CurriculumPublicationEvent,
  },
}).createMachine({
  id: 'curriculumPublication',
  initial: 'draft',
  context: {
    planId: null,
    revision: 0,
    validationIssueCount: 0,
    validationWarningCount: 0,
    serverRevision: null,
    error: null,
  },
  states: {
    draft: { on: { EDIT: 'draft', VALIDATE: 'validating', BRANCH: 'branching' } },
    validating: {
      on: {
        VALIDATION_PASSED: {
          target: 'valid',
          actions: assign({
            validationIssueCount: 0,
            validationWarningCount: ({ event }) => event.warnings,
            error: null,
          }),
        },
        VALIDATION_FAILED: {
          target: 'invalid',
          actions: assign({
            validationIssueCount: ({ event }) => event.issues,
            validationWarningCount: 0,
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    invalid: { on: { EDIT: 'draft', VALIDATE: 'validating' } },
    valid: { on: { EDIT: 'draft', PUBLISH: 'publishing', VALIDATE: 'validating' } },
    publishing: {
      on: {
        PUBLISHED: {
          target: 'published',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    published: { on: { BRANCH: 'branching' } },
    branching: {
      on: {
        BRANCHED: {
          target: 'draft',
          actions: assign({
            planId: ({ event }) => event.planId,
            revision: 0,
            validationIssueCount: 0,
            validationWarningCount: 0,
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'draft' } },
    failed: { on: { RETRY: 'validating', EDIT: 'draft' } },
  },
});

type PlanAuthoringEvent =
  | { type: 'EDIT' }
  | { type: 'SAVE' }
  | { type: 'REORDER' }
  | { type: 'SAVED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'RETRY' };

export const planAuthoringMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      operation: 'SAVE' | 'REORDER' | null;
      error: string | null;
    },
    events: {} as PlanAuthoringEvent,
  },
}).createMachine({
  id: 'planAuthoring',
  initial: 'editing',
  context: { revision: 0, serverRevision: null, operation: null, error: null },
  states: {
    editing: {
      on: {
        EDIT: { actions: assign({ error: null }) },
        SAVE: {
          target: 'saving',
          actions: assign({ operation: 'SAVE', error: null }),
        },
        REORDER: {
          target: 'reordering',
          actions: assign({ operation: 'REORDER', error: null }),
        },
      },
    },
    saving: {
      on: {
        SAVED: {
          target: 'saved',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    reordering: {
      on: {
        SAVED: {
          target: 'saved',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: { on: { EDIT: 'editing', SAVE: 'saving', REORDER: 'reordering' } },
    conflict: { on: { EDIT: 'editing' } },
    failed: { on: { RETRY: 'saving', EDIT: 'editing' } },
  },
});

type RelationshipEditorEvent =
  | { type: 'EDIT' }
  | { type: 'SYNC' }
  | { type: 'SYNCED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string };

export const relationshipEditorMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as RelationshipEditorEvent,
  },
}).createMachine({
  id: 'relationshipEditor',
  initial: 'idle',
  context: { revision: 0, serverRevision: null, error: null },
  states: {
    idle: { on: { EDIT: 'editing' } },
    editing: { on: { SYNC: 'syncing' } },
    syncing: {
      on: {
        SYNCED: {
          target: 'idle',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'editing' } },
    failed: { on: { EDIT: 'editing' } },
  },
});

type TaskDependencyEvent =
  | { type: 'EDIT' }
  | { type: 'SAVE'; sourceTaskId: number; targetTaskId: number }
  | { type: 'SAVED'; revision: number }
  | { type: 'CYCLE_REJECTED'; message: string }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string };

export const taskDependencyMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as TaskDependencyEvent,
  },
  guards: {
    isDirectedEdge: ({ event }) =>
      event.type === 'SAVE' && event.sourceTaskId !== event.targetTaskId,
  },
}).createMachine({
  id: 'taskDependency',
  initial: 'editing',
  context: { revision: 0, serverRevision: null, error: null },
  states: {
    editing: {
      on: {
        EDIT: {},
        SAVE: [
          { guard: 'isDirectedEdge', target: 'saving' },
          {
            target: 'cycleRejected',
            actions: assign({ error: 'A task cannot depend on itself.' }),
          },
        ],
      },
    },
    saving: {
      on: {
        SAVED: {
          target: 'saved',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CYCLE_REJECTED: {
          target: 'cycleRejected',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: {
      on: {
        EDIT: 'editing',
        SAVE: [
          { guard: 'isDirectedEdge', target: 'saving' },
          {
            target: 'cycleRejected',
            actions: assign({ error: 'A task cannot depend on itself.' }),
          },
        ],
      },
    },
    cycleRejected: { on: { EDIT: 'editing' } },
    conflict: { on: { EDIT: 'editing' } },
    failed: { on: { EDIT: 'editing' } },
  },
});

type RevisionedEditorEvent =
  | { type: 'EDIT' }
  | { type: 'SAVE' }
  | { type: 'PUBLISH' }
  | { type: 'ARCHIVE' }
  | { type: 'VALIDATION_PASSED' }
  | { type: 'VALIDATION_FAILED'; message: string }
  | { type: 'SAVED'; revision: number }
  | { type: 'PUBLISHED'; revision: number }
  | { type: 'ARCHIVED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string };

const createRevisionedEditorMachine = (
  id:
    | 'outcomeLibrary'
    | 'learningResource'
    | 'resourceVersion'
    | 'ratingScale'
    | 'rubricBuilder'
    | 'room',
) =>
  setup({
    types: {
      context: {} as {
        revision: number;
        serverRevision: number | null;
        error: string | null;
      },
      events: {} as RevisionedEditorEvent,
    },
  }).createMachine({
    id,
    initial: 'editing',
    context: { revision: 0, serverRevision: null, error: null },
    states: {
      editing: {
        on: {
          EDIT: { actions: assign({ error: null }) },
          SAVE: 'saving',
          PUBLISH: 'validating',
          ARCHIVE: 'archiving',
        },
      },
      saving: {
        on: {
          SAVED: {
            target: 'saved',
            actions: assign({ revision: ({ event }) => event.revision, error: null }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      validating: {
        on: {
          VALIDATION_PASSED: 'publishing',
          VALIDATION_FAILED: {
            target: 'invalid',
            actions: assign({ error: ({ event }) => event.message }),
          },
          PUBLISHED: {
            target: 'published',
            actions: assign({ revision: ({ event }) => event.revision, error: null }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      publishing: {
        on: {
          PUBLISHED: {
            target: 'published',
            actions: assign({ revision: ({ event }) => event.revision, error: null }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      archiving: {
        on: {
          ARCHIVED: {
            target: 'archived',
            actions: assign({ revision: ({ event }) => event.revision, error: null }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      saved: {
        on: {
          EDIT: 'editing',
          SAVE: 'saving',
          PUBLISH: 'validating',
          ARCHIVE: 'archiving',
        },
      },
      invalid: { on: { EDIT: 'editing', PUBLISH: 'validating' } },
      published: {},
      archived: {},
      conflict: { on: { EDIT: 'editing' } },
      failed: {
        on: {
          EDIT: 'editing',
          SAVE: 'saving',
          PUBLISH: 'validating',
          ARCHIVE: 'archiving',
        },
      },
    },
  });

export const outcomeLibraryMachine = createRevisionedEditorMachine('outcomeLibrary');
export const learningResourceMachine = createRevisionedEditorMachine('learningResource');
export const resourceVersionMachine = createRevisionedEditorMachine('resourceVersion');
export const ratingScaleMachine = createRevisionedEditorMachine('ratingScale');
export const rubricBuilderMachine = createRevisionedEditorMachine('rubricBuilder');
export const roomMachine = createRevisionedEditorMachine('room');

type OperationalStatus = 'DRAFT' | 'ENROLLMENT_OPEN' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

type OperationalLifecycleEvent =
  | { type: 'HYDRATE'; status: OperationalStatus; revision: number }
  | { type: 'SAVE' }
  | { type: 'SAVED'; revision: number }
  | { type: 'TRANSITION'; targetStatus: OperationalStatus }
  | { type: 'TRANSITIONED'; status: OperationalStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

const createOperationalLifecycleMachine = (id: 'studyCycle' | 'classGroup') =>
  setup({
    types: {
      context: {} as {
        status: OperationalStatus;
        revision: number;
        pendingStatus: OperationalStatus | null;
        serverRevision: number | null;
        error: string | null;
      },
      events: {} as OperationalLifecycleEvent,
    },
    guards: {
      mayTransition: ({ context, event }) => {
        if (event.type !== 'TRANSITION') return false;
        const allowed: Record<OperationalStatus, readonly OperationalStatus[]> =
          id === 'studyCycle'
            ? {
                DRAFT: ['ENROLLMENT_OPEN', 'CANCELLED'],
                ENROLLMENT_OPEN: ['ACTIVE', 'CANCELLED'],
                ACTIVE: ['COMPLETED', 'CANCELLED'],
                COMPLETED: [],
                CANCELLED: [],
              }
            : {
                DRAFT: ['ACTIVE', 'CANCELLED'],
                ENROLLMENT_OPEN: [],
                ACTIVE: ['COMPLETED', 'CANCELLED'],
                COMPLETED: [],
                CANCELLED: [],
              };
        return allowed[context.status].includes(event.targetStatus);
      },
    },
  }).createMachine({
    id,
    initial: 'loading',
    context: {
      status: 'DRAFT',
      revision: 0,
      pendingStatus: null,
      serverRevision: null,
      error: null,
    },
    states: {
      loading: {
        on: {
          HYDRATE: {
            target: 'ready',
            actions: assign({
              status: ({ event }) => event.status,
              revision: ({ event }) => event.revision,
            }),
          },
        },
      },
      ready: {
        on: {
          HYDRATE: {
            actions: assign({
              status: ({ event }) => event.status,
              revision: ({ event }) => event.revision,
              error: null,
            }),
          },
          SAVE: 'saving',
          TRANSITION: {
            guard: 'mayTransition',
            target: 'transitioning',
            actions: assign({
              pendingStatus: ({ event }) => event.targetStatus,
              error: null,
            }),
          },
        },
      },
      saving: {
        on: {
          SAVED: {
            target: 'ready',
            actions: assign({ revision: ({ event }) => event.revision, error: null }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      transitioning: {
        on: {
          TRANSITIONED: {
            target: 'ready',
            actions: assign({
              status: ({ event }) => event.status,
              revision: ({ event }) => event.revision,
              pendingStatus: null,
              error: null,
            }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      conflict: {
        on: {
          HYDRATE: {
            target: 'ready',
            actions: assign({
              status: ({ event }) => event.status,
              revision: ({ event }) => event.revision,
              serverRevision: null,
            }),
          },
        },
      },
      failed: {
        on: {
          EDIT: 'ready',
          SAVE: 'saving',
          TRANSITION: {
            guard: 'mayTransition',
            target: 'transitioning',
            actions: assign({
              pendingStatus: ({ event }) => event.targetStatus,
              error: null,
            }),
          },
        },
      },
    },
  });

export const studyCycleMachine = createOperationalLifecycleMachine('studyCycle');
export const classGroupMachine = createOperationalLifecycleMachine('classGroup');

type PersonStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ARCHIVED';

type PersonLifecycleEvent =
  | { type: 'HYDRATE'; status: PersonStatus; revision: number }
  | { type: 'SAVE' }
  | { type: 'SAVED'; revision: number }
  | { type: 'TRANSITION'; targetStatus: PersonStatus }
  | { type: 'TRANSITIONED'; status: PersonStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const personLifecycleMachine = setup({
  types: {
    context: {} as {
      status: PersonStatus;
      revision: number;
      pendingStatus: PersonStatus | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as PersonLifecycleEvent,
  },
  guards: {
    mayTransition: ({ context, event }) => {
      if (event.type !== 'TRANSITION') return false;
      const allowed: Record<PersonStatus, readonly PersonStatus[]> = {
        ACTIVE: ['INACTIVE', 'SUSPENDED', 'ARCHIVED'],
        INACTIVE: ['ACTIVE', 'ARCHIVED'],
        SUSPENDED: ['ACTIVE', 'INACTIVE', 'ARCHIVED'],
        ARCHIVED: [],
      };
      return allowed[context.status].includes(event.targetStatus);
    },
  },
}).createMachine({
  id: 'personLifecycle',
  initial: 'loading',
  context: {
    status: 'INACTIVE',
    revision: 0,
    pendingStatus: null,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
      },
    },
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        SAVE: 'saving',
        TRANSITION: {
          guard: 'mayTransition',
          target: 'transitioning',
          actions: assign({ pendingStatus: ({ event }) => event.targetStatus, error: null }),
        },
      },
    },
    saving: {
      on: {
        SAVED: {
          target: 'ready',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    transitioning: {
      on: {
        TRANSITIONED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            pendingStatus: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            serverRevision: null,
          }),
        },
      },
    },
    failed: { on: { EDIT: 'ready' } },
  },
});

type TeacherAssignmentEvent =
  | { type: 'ASSIGN' }
  | { type: 'END'; revision: number }
  | { type: 'TRANSFER'; revision: number }
  | { type: 'SUCCEEDED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const teacherAssignmentMachine = setup({
  types: {
    context: {} as {
      operation: 'ASSIGN' | 'END' | 'TRANSFER' | null;
      revision: number;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as TeacherAssignmentEvent,
  },
}).createMachine({
  id: 'teacherAssignment',
  initial: 'ready',
  context: { operation: null, revision: 0, serverRevision: null, error: null },
  states: {
    ready: {
      on: {
        ASSIGN: {
          target: 'saving',
          actions: assign({ operation: 'ASSIGN', error: null }),
        },
        END: {
          target: 'saving',
          actions: assign({
            operation: 'END',
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        TRANSFER: {
          target: 'saving',
          actions: assign({
            operation: 'TRANSFER',
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type EnrollmentStatus =
  'PENDING' | 'ACTIVE' | 'COMPLETED' | 'WITHDRAWN' | 'CANCELLED' | 'TRANSFERRED';

type EnrollmentMovementEvent =
  | { type: 'HYDRATE'; status: EnrollmentStatus; revision: number }
  | { type: 'ACTIVATE' }
  | { type: 'WITHDRAW' }
  | { type: 'TRANSFER' }
  | { type: 'SUCCEEDED'; status: EnrollmentStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const enrollmentMovementMachine = setup({
  types: {
    context: {} as {
      status: EnrollmentStatus;
      revision: number;
      operation: 'ACTIVATE' | 'WITHDRAW' | 'TRANSFER' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as EnrollmentMovementEvent,
  },
  guards: {
    mayActivate: ({ context }) => context.status === 'PENDING',
    mayWithdraw: ({ context }) => ['PENDING', 'ACTIVE'].includes(context.status),
    mayTransfer: ({ context }) => context.status === 'ACTIVE',
  },
}).createMachine({
  id: 'enrollmentMovement',
  initial: 'loading',
  context: {
    status: 'PENDING',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
      },
    },
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        ACTIVATE: {
          guard: 'mayActivate',
          target: 'saving',
          actions: assign({ operation: 'ACTIVATE', error: null }),
        },
        WITHDRAW: {
          guard: 'mayWithdraw',
          target: 'saving',
          actions: assign({ operation: 'WITHDRAW', error: null }),
        },
        TRANSFER: {
          guard: 'mayTransfer',
          target: 'saving',
          actions: assign({ operation: 'TRANSFER', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type InvitationStatus =
  'DRAFT' | 'PENDING' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'REVOKED' | 'EXPIRED' | 'FAILED';

type InvitationEvent =
  | { type: 'HYDRATE'; status: InvitationStatus; revision: number }
  | { type: 'SEND' }
  | { type: 'RESEND' }
  | { type: 'REVOKE' }
  | { type: 'ACCEPT' }
  | { type: 'SUCCEEDED'; status: InvitationStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const invitationOnboardingMachine = setup({
  types: {
    context: {} as {
      status: InvitationStatus;
      revision: number;
      operation: 'SEND' | 'RESEND' | 'REVOKE' | 'ACCEPT' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as InvitationEvent,
  },
  guards: {
    maySend: ({ context }) => context.status === 'DRAFT',
    mayResend: ({ context }) => ['PENDING', 'SENT', 'FAILED'].includes(context.status),
    mayRevoke: ({ context }) => ['DRAFT', 'PENDING', 'SENT', 'FAILED'].includes(context.status),
    mayAccept: ({ context }) => context.status === 'SENT',
  },
}).createMachine({
  id: 'invitationOnboarding',
  initial: 'loading',
  context: {
    status: 'DRAFT',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
      },
    },
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        SEND: {
          guard: 'maySend',
          target: 'saving',
          actions: assign({ operation: 'SEND', error: null }),
        },
        RESEND: {
          guard: 'mayResend',
          target: 'saving',
          actions: assign({ operation: 'RESEND', error: null }),
        },
        REVOKE: {
          guard: 'mayRevoke',
          target: 'saving',
          actions: assign({ operation: 'REVOKE', error: null }),
        },
        ACCEPT: {
          guard: 'mayAccept',
          target: 'saving',
          actions: assign({ operation: 'ACCEPT', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { HYDRATE: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type AccountStatus = 'PENDING_VERIFICATION' | 'ACTIVE' | 'LOCKED' | 'SUSPENDED' | 'CLOSED';
type AccountLifecycleEvent =
  | { type: 'HYDRATE'; status: AccountStatus; revision: number }
  | { type: 'SUSPEND' }
  | { type: 'REACTIVATE' }
  | { type: 'SUCCEEDED'; status: AccountStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const accountLifecycleMachine = setup({
  types: {
    context: {} as {
      status: AccountStatus;
      revision: number;
      operation: 'SUSPEND' | 'REACTIVATE' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as AccountLifecycleEvent,
  },
  guards: {
    maySuspend: ({ context }) => context.status === 'ACTIVE',
    mayReactivate: ({ context }) => context.status === 'SUSPENDED',
  },
}).createMachine({
  id: 'accountLifecycle',
  initial: 'loading',
  context: {
    status: 'PENDING_VERIFICATION',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
      },
    },
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        SUSPEND: {
          guard: 'maySuspend',
          target: 'saving',
          actions: assign({ operation: 'SUSPEND', error: null }),
        },
        REACTIVATE: {
          guard: 'mayReactivate',
          target: 'saving',
          actions: assign({ operation: 'REACTIVATE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { HYDRATE: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

const createProviderActionMachine = (
  id:
    | 'invitationTokenDelivery'
    | 'passwordAction'
    | 'roleAssignment'
    | 'sessionRevocation'
    | 'accountToken'
    | 'externalIdentity'
    | 'mfaEnrollment'
    | 'mfaRecovery',
) =>
  setup({
    types: {
      context: {} as {
        revision: number;
        operation: string | null;
        error: string | null;
      },
      events: {} as
        | { type: 'START'; operation: string }
        | { type: 'SUCCEEDED'; revision?: number }
        | { type: 'FAILED'; message: string }
        | { type: 'RETRY' }
        | { type: 'RESET' },
    },
  }).createMachine({
    id,
    initial: 'ready',
    context: { revision: 0, operation: null, error: null },
    states: {
      ready: {
        on: {
          START: {
            target: 'submitting',
            actions: assign({
              operation: ({ event }) => event.operation,
              error: null,
            }),
          },
        },
      },
      submitting: {
        on: {
          SUCCEEDED: {
            target: 'succeeded',
            actions: assign({
              revision: ({ context, event }) => event.revision ?? context.revision,
              error: null,
            }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      succeeded: {
        on: {
          RESET: 'ready',
          START: {
            target: 'submitting',
            actions: assign({
              operation: ({ event }) => event.operation,
              error: null,
            }),
          },
        },
      },
      failed: { on: { RETRY: 'submitting', RESET: 'ready' } },
    },
  });

export const invitationTokenDeliveryMachine =
  createProviderActionMachine('invitationTokenDelivery');
export const passwordActionMachine = createProviderActionMachine('passwordAction');

type RoleAssignmentEvent =
  | { type: 'LOADED' }
  | { type: 'LOAD_FAILED'; message: string }
  | { type: 'GRANT' }
  | { type: 'REVOKE'; assignmentId: number; revision: number }
  | { type: 'CANCEL' }
  | { type: 'CONFIRM' }
  | { type: 'SUCCEEDED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FORBIDDEN'; message: string }
  | { type: 'FAILED'; message: string }
  | { type: 'RETRY' }
  | { type: 'RESET' }
  | { type: 'RELOAD' };

export const roleAssignmentMachine = setup({
  types: {
    context: {} as {
      operation: 'GRANT' | 'REVOKE' | null;
      assignmentId: number | null;
      revision: number;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as RoleAssignmentEvent,
  },
  guards: {
    hasPendingOperation: ({ context }) => context.operation !== null,
  },
  actions: {
    clearOperation: assign({
      operation: null,
      assignmentId: null,
      serverRevision: null,
      error: null,
    }),
  },
}).createMachine({
  id: 'roleAssignment',
  initial: 'loading',
  context: {
    operation: null,
    assignmentId: null,
    revision: 0,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        LOADED: { target: 'ready', actions: 'clearOperation' },
        LOAD_FAILED: {
          target: 'retry',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    ready: {
      on: {
        GRANT: {
          target: 'grantConfirmation',
          actions: assign({
            operation: 'GRANT',
            assignmentId: null,
            error: null,
          }),
        },
        REVOKE: {
          target: 'revokeConfirmation',
          actions: assign({
            operation: 'REVOKE',
            assignmentId: ({ event }) => event.assignmentId,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        RELOAD: 'loading',
      },
    },
    grantConfirmation: {
      on: {
        CONFIRM: 'submitting',
        CANCEL: { target: 'ready', actions: 'clearOperation' },
      },
    },
    revokeConfirmation: {
      on: {
        CONFIRM: 'submitting',
        CANCEL: { target: 'ready', actions: 'clearOperation' },
      },
    },
    submitting: {
      on: {
        SUCCEEDED: {
          target: 'success',
          actions: assign({
            revision: ({ event }) => event.revision,
            serverRevision: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            serverRevision: ({ event }) => event.serverRevision,
            error: 'The role assignment changed. Reload before trying again.',
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FAILED: {
          target: 'retry',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    success: {
      on: {
        RESET: { target: 'ready', actions: 'clearOperation' },
        RELOAD: 'loading',
      },
    },
    conflict: {
      on: {
        RELOAD: 'loading',
        RESET: { target: 'ready', actions: 'clearOperation' },
      },
    },
    forbidden: {
      on: {
        RESET: { target: 'ready', actions: 'clearOperation' },
        RELOAD: 'loading',
      },
    },
    retry: {
      on: {
        RETRY: [{ guard: 'hasPendingOperation', target: 'submitting' }, { target: 'loading' }],
        RESET: { target: 'ready', actions: 'clearOperation' },
      },
    },
  },
});

export const sessionRevocationMachine = createProviderActionMachine('sessionRevocation');
export const accountTokenMachine = createProviderActionMachine('accountToken');
export const externalIdentityMachine = createProviderActionMachine('externalIdentity');
export const mfaEnrollmentMachine = createProviderActionMachine('mfaEnrollment');
export const mfaRecoveryMachine = setup({
  types: {
    context: {} as {
      selectedPath: 'BACKUP_FACTOR' | 'VERIFIED_EMAIL' | null;
    },
    events: {} as
      | { type: 'PROVIDER_UNSUPPORTED' }
      | { type: 'PROVIDER_SUPPORTED' }
      | { type: 'SELECT_BACKUP_FACTOR' }
      | { type: 'SELECT_VERIFIED_EMAIL' }
      | { type: 'RESET' },
  },
}).createMachine({
  id: 'mfaRecovery',
  initial: 'checkingCapability',
  context: { selectedPath: null },
  states: {
    checkingCapability: {
      on: {
        PROVIDER_UNSUPPORTED: 'recoveryChoice',
        PROVIDER_SUPPORTED: 'providerContractMismatch',
      },
    },
    recoveryChoice: {
      on: {
        SELECT_BACKUP_FACTOR: {
          target: 'backupFactorGuidance',
          actions: assign({ selectedPath: 'BACKUP_FACTOR' }),
        },
        SELECT_VERIFIED_EMAIL: {
          target: 'verifiedEmailGuidance',
          actions: assign({ selectedPath: 'VERIFIED_EMAIL' }),
        },
      },
    },
    backupFactorGuidance: {
      on: {
        SELECT_VERIFIED_EMAIL: {
          target: 'verifiedEmailGuidance',
          actions: assign({ selectedPath: 'VERIFIED_EMAIL' }),
        },
        RESET: {
          target: 'recoveryChoice',
          actions: assign({ selectedPath: null }),
        },
      },
    },
    verifiedEmailGuidance: {
      on: {
        SELECT_BACKUP_FACTOR: {
          target: 'backupFactorGuidance',
          actions: assign({ selectedPath: 'BACKUP_FACTOR' }),
        },
        RESET: {
          target: 'recoveryChoice',
          actions: assign({ selectedPath: null }),
        },
      },
    },
    providerContractMismatch: {},
  },
});
export const projectionQueryMachine = createQueryMachine<unknown>();

type GuardianLifecycleStatus =
  'INVITED' | 'PENDING_VERIFICATION' | 'VERIFIED' | 'SUSPENDED' | 'ARCHIVED';
type GuardianVerificationEvent =
  | { type: 'HYDRATE'; status: GuardianLifecycleStatus; revision: number }
  | { type: 'UPDATE' }
  | { type: 'VERIFY' }
  | { type: 'SUCCEEDED'; status: GuardianLifecycleStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const guardianVerificationMachine = setup({
  types: {
    context: {} as {
      status: GuardianLifecycleStatus;
      revision: number;
      operation: 'UPDATE' | 'VERIFY' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as GuardianVerificationEvent,
  },
  guards: {
    mayVerify: ({ context }) =>
      context.status === 'INVITED' || context.status === 'PENDING_VERIFICATION',
  },
}).createMachine({
  id: 'guardianVerification',
  initial: 'loading',
  context: {
    status: 'INVITED',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
      },
    },
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        UPDATE: {
          target: 'saving',
          actions: assign({ operation: 'UPDATE', error: null }),
        },
        VERIFY: {
          guard: 'mayVerify',
          target: 'saving',
          actions: assign({ operation: 'VERIFY', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    conflict: { on: { HYDRATE: 'loading' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type GuardianAuthorityStatus = 'UNVERIFIED' | 'ACTIVE' | 'ENDED';
type GuardianAuthorityEvent =
  | { type: 'HYDRATE'; status: GuardianAuthorityStatus; revision: number }
  | { type: 'LINK' }
  | { type: 'VERIFY' }
  | { type: 'END' }
  | { type: 'SUCCEEDED'; status: GuardianAuthorityStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const guardianAuthorityMachine = setup({
  types: {
    context: {} as {
      status: GuardianAuthorityStatus;
      revision: number;
      operation: 'LINK' | 'VERIFY' | 'END' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as GuardianAuthorityEvent,
  },
  guards: {
    mayVerify: ({ context }) => context.status === 'UNVERIFIED',
    mayEnd: ({ context }) => context.status !== 'ENDED',
  },
}).createMachine({
  id: 'guardianAuthority',
  initial: 'ready',
  context: {
    status: 'UNVERIFIED',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        LINK: { target: 'saving', actions: assign({ operation: 'LINK', error: null }) },
        VERIFY: {
          guard: 'mayVerify',
          target: 'saving',
          actions: assign({ operation: 'VERIFY', error: null }),
        },
        END: {
          guard: 'mayEnd',
          target: 'saving',
          actions: assign({ operation: 'END', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type ConsentDecision = 'NONE' | 'GRANTED' | 'DENIED' | 'WITHDRAWN';
type ConsentDecisionEvent =
  | { type: 'HYDRATE'; decision: ConsentDecision }
  | { type: 'GRANT' }
  | { type: 'DENY' }
  | { type: 'WITHDRAW' }
  | { type: 'SUCCEEDED'; decision: ConsentDecision }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const consentDecisionMachine = setup({
  types: {
    context: {} as {
      decision: ConsentDecision;
      operation: 'GRANT' | 'DENY' | 'WITHDRAW' | null;
      error: string | null;
    },
    events: {} as ConsentDecisionEvent,
  },
  guards: {
    mayWithdraw: ({ context }) => context.decision === 'GRANTED',
  },
}).createMachine({
  id: 'consentDecision',
  initial: 'ready',
  context: { decision: 'NONE', operation: null, error: null },
  states: {
    ready: {
      on: {
        HYDRATE: { actions: assign({ decision: ({ event }) => event.decision }) },
        GRANT: { target: 'saving', actions: assign({ operation: 'GRANT', error: null }) },
        DENY: { target: 'saving', actions: assign({ operation: 'DENY', error: null }) },
        WITHDRAW: {
          guard: 'mayWithdraw',
          target: 'saving',
          actions: assign({ operation: 'WITHDRAW', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            decision: ({ event }) => event.decision,
            operation: null,
            error: null,
          }),
        },
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    failed: { on: { EDIT: 'ready' } },
  },
});

type LegalHoldEvent =
  | { type: 'HYDRATE'; status: 'ACTIVE' | 'RELEASED'; revision: number }
  | { type: 'CREATE' }
  | { type: 'ADD_TARGET' }
  | { type: 'REMOVE_TARGET' }
  | { type: 'RELEASE' }
  | { type: 'SUCCEEDED'; status: 'ACTIVE' | 'RELEASED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const legalHoldMachine = setup({
  types: {
    context: {} as {
      status: 'ACTIVE' | 'RELEASED';
      revision: number;
      operation: 'CREATE' | 'ADD_TARGET' | 'REMOVE_TARGET' | 'RELEASE' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as LegalHoldEvent,
  },
  guards: { isActive: ({ context }) => context.status === 'ACTIVE' },
}).createMachine({
  id: 'legalHold',
  initial: 'ready',
  context: {
    status: 'ACTIVE',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        CREATE: { target: 'saving', actions: assign({ operation: 'CREATE', error: null }) },
        ADD_TARGET: {
          guard: 'isActive',
          target: 'saving',
          actions: assign({ operation: 'ADD_TARGET', error: null }),
        },
        REMOVE_TARGET: {
          guard: 'isActive',
          target: 'saving',
          actions: assign({ operation: 'REMOVE_TARGET', error: null }),
        },
        RELEASE: {
          guard: 'isActive',
          target: 'saving',
          actions: assign({ operation: 'RELEASE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type PrivacyRequestStatus =
  | 'RECEIVED'
  | 'IDENTITY_PENDING'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'CANCELLED';
type PrivacyRequestEvent =
  | { type: 'HYDRATE'; status: PrivacyRequestStatus; revision: number }
  | { type: 'SUBMIT' }
  | { type: 'VERIFY' }
  | { type: 'ASSIGN' }
  | { type: 'COMPLETE' }
  | { type: 'SUCCEEDED'; status: PrivacyRequestStatus; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' };

export const privacyRequestMachine = setup({
  types: {
    context: {} as {
      status: PrivacyRequestStatus;
      revision: number;
      operation: 'SUBMIT' | 'VERIFY' | 'ASSIGN' | 'COMPLETE' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as PrivacyRequestEvent,
  },
  guards: {
    mayVerify: ({ context }) => ['RECEIVED', 'IDENTITY_PENDING'].includes(context.status),
    mayAssign: ({ context }) => context.status === 'IN_REVIEW',
    mayComplete: ({ context }) => ['IN_REVIEW', 'APPROVED', 'PROCESSING'].includes(context.status),
  },
}).createMachine({
  id: 'privacyRequest',
  initial: 'ready',
  context: {
    status: 'RECEIVED',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
          }),
        },
        SUBMIT: { target: 'saving', actions: assign({ operation: 'SUBMIT', error: null }) },
        VERIFY: {
          guard: 'mayVerify',
          target: 'saving',
          actions: assign({ operation: 'VERIFY', error: null }),
        },
        ASSIGN: {
          guard: 'mayAssign',
          target: 'saving',
          actions: assign({ operation: 'ASSIGN', error: null }),
        },
        COMPLETE: {
          guard: 'mayComplete',
          target: 'saving',
          actions: assign({ operation: 'COMPLETE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            operation: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

export const messagingMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      revisionGap: boolean;
      error: string | null;
      failedOperation: 'ARCHIVE' | 'RESTORE' | 'CLOSE' | null;
      operationRetryable: boolean;
      resumeLifecycle: 'active' | 'archived';
      accessLoss: 'MEMBERSHIP_LOST' | 'BLOCKED' | null;
      pendingMessages: Array<{
        clientMessageId: string;
        bodyText: string;
        messageId: number | null;
        status: 'OPTIMISTIC' | 'ACKNOWLEDGED' | 'FAILED';
        retryable: boolean;
        retryCount: number;
        error: string | null;
      }>;
    },
    events: {} as
      | {
          type: 'HYDRATE';
          revision: number;
          archived: boolean;
          status: 'ACTIVE' | 'ARCHIVED' | 'CLOSED';
        }
      | { type: 'ARCHIVE' }
      | { type: 'CLOSE' }
      | { type: 'RESTORE' }
      | { type: 'SUCCEEDED'; revision: number }
      | { type: 'FAILED'; message: string; retryable: boolean }
      | { type: 'RETRY' }
      | { type: 'DISMISS_ERROR' }
      | { type: 'REALTIME_REVISION'; revision: number }
      | { type: 'RECONCILED'; revision: number }
      | { type: 'REMOTE_CLOSED'; revision: number }
      | { type: 'MEMBERSHIP_LOST' }
      | { type: 'MEMBERSHIP_RESTORED' }
      | { type: 'BLOCK_CHANGED'; isBlocked: boolean }
      | { type: 'OPTIMISTIC_SEND'; clientMessageId: string; bodyText: string }
      | { type: 'SERVER_ACKNOWLEDGED'; clientMessageId: string; messageId: number }
      | { type: 'SERVER_RECONCILED'; clientMessageId: string }
      | {
          type: 'SEND_FAILED';
          clientMessageId: string;
          message: string;
          retryable: boolean;
        }
      | { type: 'RETRY_SEND'; clientMessageId: string },
  },
  guards: {
    hydratedClosed: ({ event }) => event.type === 'HYDRATE' && event.status === 'CLOSED',
    hydratedArchived: ({ event }) =>
      event.type === 'HYDRATE' &&
      (event.status === 'ARCHIVED' || (event.status === 'ACTIVE' && event.archived)),
    mayMutate: ({ context }) => context.accessLoss === null && !context.revisionGap,
    retryArchive: ({ context }) =>
      context.failedOperation === 'ARCHIVE' && context.operationRetryable,
    retryRestore: ({ context }) =>
      context.failedOperation === 'RESTORE' && context.operationRetryable,
    retryClose: ({ context }) => context.failedOperation === 'CLOSE' && context.operationRetryable,
    failedRestore: ({ context }) => context.failedOperation === 'RESTORE',
    failedCloseFromArchive: ({ context }) =>
      context.failedOperation === 'CLOSE' && context.resumeLifecycle === 'archived',
  },
  actions: {
    hydrate: assign({
      revision: ({ event }) => (event.type === 'HYDRATE' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      failedOperation: null,
      operationRetryable: false,
      resumeLifecycle: ({ event }) =>
        event.type === 'HYDRATE' && event.archived ? 'archived' : 'active',
    }),
    archiveStarted: assign({
      failedOperation: 'ARCHIVE',
      resumeLifecycle: 'active',
      error: null,
      operationRetryable: false,
    }),
    restoreStarted: assign({
      failedOperation: 'RESTORE',
      resumeLifecycle: 'archived',
      error: null,
      operationRetryable: false,
    }),
    closeFromActiveStarted: assign({
      failedOperation: 'CLOSE',
      resumeLifecycle: 'active',
      error: null,
      operationRetryable: false,
    }),
    closeFromArchiveStarted: assign({
      failedOperation: 'CLOSE',
      resumeLifecycle: 'archived',
      error: null,
      operationRetryable: false,
    }),
    operationSucceeded: assign({
      revision: ({ event }) => (event.type === 'SUCCEEDED' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      failedOperation: null,
      operationRetryable: false,
    }),
    operationFailed: assign({
      error: ({ event }) => (event.type === 'FAILED' ? event.message : null),
      operationRetryable: ({ event }) => event.type === 'FAILED' && event.retryable,
    }),
    retryStarted: assign({ error: null, operationRetryable: false }),
    dismissError: assign({
      error: null,
      failedOperation: null,
      operationRetryable: false,
    }),
    realtimeRevision: assign({
      serverRevision: ({ context, event }) =>
        event.type === 'REALTIME_REVISION'
          ? Math.max(context.serverRevision ?? 0, event.revision)
          : context.serverRevision,
      revisionGap: ({ context, event }) =>
        event.type === 'REALTIME_REVISION' && event.revision > context.revision + 1,
      revision: ({ context, event }) =>
        event.type === 'REALTIME_REVISION' && event.revision === context.revision + 1
          ? event.revision
          : context.revision,
    }),
    reconciled: assign({
      revision: ({ event }) => (event.type === 'RECONCILED' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
    }),
    remotelyClosed: assign({
      revision: ({ event }) => (event.type === 'REMOTE_CLOSED' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      failedOperation: null,
      operationRetryable: false,
    }),
    membershipLost: assign({ accessLoss: 'MEMBERSHIP_LOST' }),
    membershipRestored: assign({
      accessLoss: ({ context }) =>
        context.accessLoss === 'MEMBERSHIP_LOST' ? null : context.accessLoss,
    }),
    blockChanged: assign({
      accessLoss: ({ context, event }) =>
        event.type === 'BLOCK_CHANGED'
          ? event.isBlocked
            ? 'BLOCKED'
            : context.accessLoss === 'BLOCKED'
              ? null
              : context.accessLoss
          : context.accessLoss,
    }),
    optimisticSend: assign({
      pendingMessages: ({ context, event }) =>
        event.type === 'OPTIMISTIC_SEND'
          ? [
              ...context.pendingMessages.filter(
                (message) => message.clientMessageId !== event.clientMessageId,
              ),
              {
                clientMessageId: event.clientMessageId,
                bodyText: event.bodyText,
                messageId: null,
                status: 'OPTIMISTIC' as const,
                retryable: false,
                retryCount: 0,
                error: null,
              },
            ]
          : context.pendingMessages,
    }),
    serverAcknowledged: assign({
      pendingMessages: ({ context, event }) =>
        event.type === 'SERVER_ACKNOWLEDGED'
          ? context.pendingMessages.map((message) =>
              message.clientMessageId === event.clientMessageId
                ? {
                    ...message,
                    messageId: event.messageId,
                    status: 'ACKNOWLEDGED' as const,
                    error: null,
                  }
                : message,
            )
          : context.pendingMessages,
    }),
    serverReconciled: assign({
      pendingMessages: ({ context, event }) =>
        event.type === 'SERVER_RECONCILED'
          ? context.pendingMessages.filter(
              (message) => message.clientMessageId !== event.clientMessageId,
            )
          : context.pendingMessages,
    }),
    sendFailed: assign({
      pendingMessages: ({ context, event }) =>
        event.type === 'SEND_FAILED'
          ? context.pendingMessages.map((message) =>
              message.clientMessageId === event.clientMessageId
                ? {
                    ...message,
                    status: 'FAILED' as const,
                    retryable: event.retryable,
                    error: event.message,
                  }
                : message,
            )
          : context.pendingMessages,
    }),
    retrySend: assign({
      pendingMessages: ({ context, event }) =>
        event.type === 'RETRY_SEND'
          ? context.pendingMessages.map((message) =>
              message.clientMessageId === event.clientMessageId && message.retryable
                ? {
                    ...message,
                    status: 'OPTIMISTIC' as const,
                    retryCount: message.retryCount + 1,
                    error: null,
                  }
                : message,
            )
          : context.pendingMessages,
    }),
  },
}).createMachine({
  id: 'messaging',
  initial: 'active',
  context: {
    revision: 0,
    serverRevision: null,
    revisionGap: false,
    error: null,
    failedOperation: null,
    operationRetryable: false,
    resumeLifecycle: 'active',
    accessLoss: null,
    pendingMessages: [],
  },
  on: {
    REALTIME_REVISION: { actions: 'realtimeRevision' },
    RECONCILED: { actions: 'reconciled' },
    REMOTE_CLOSED: { target: '.closed', actions: 'remotelyClosed' },
    MEMBERSHIP_LOST: { actions: 'membershipLost' },
    MEMBERSHIP_RESTORED: { actions: 'membershipRestored' },
    BLOCK_CHANGED: { actions: 'blockChanged' },
    OPTIMISTIC_SEND: { guard: 'mayMutate', actions: 'optimisticSend' },
    SERVER_ACKNOWLEDGED: { actions: 'serverAcknowledged' },
    SERVER_RECONCILED: { actions: 'serverReconciled' },
    SEND_FAILED: { actions: 'sendFailed' },
    RETRY_SEND: { guard: 'mayMutate', actions: 'retrySend' },
  },
  states: {
    active: {
      on: {
        HYDRATE: [
          { guard: 'hydratedClosed', target: 'closed', actions: 'hydrate' },
          { guard: 'hydratedArchived', target: 'archived', actions: 'hydrate' },
          { actions: 'hydrate' },
        ],
        ARCHIVE: { guard: 'mayMutate', target: 'archiving', actions: 'archiveStarted' },
        CLOSE: { guard: 'mayMutate', target: 'closing', actions: 'closeFromActiveStarted' },
      },
    },
    archiving: {
      on: {
        SUCCEEDED: {
          target: 'archived',
          actions: 'operationSucceeded',
        },
        FAILED: { target: 'failed', actions: 'operationFailed' },
      },
    },
    archived: {
      on: {
        RESTORE: { guard: 'mayMutate', target: 'restoring', actions: 'restoreStarted' },
        CLOSE: { guard: 'mayMutate', target: 'closing', actions: 'closeFromArchiveStarted' },
      },
    },
    restoring: {
      on: {
        SUCCEEDED: {
          target: 'active',
          actions: 'operationSucceeded',
        },
        FAILED: { target: 'failed', actions: 'operationFailed' },
      },
    },
    closing: {
      on: {
        SUCCEEDED: {
          target: 'closed',
          actions: 'operationSucceeded',
        },
        FAILED: { target: 'failed', actions: 'operationFailed' },
      },
    },
    failed: {
      on: {
        RETRY: [
          { guard: 'retryArchive', target: 'archiving', actions: 'retryStarted' },
          { guard: 'retryRestore', target: 'restoring', actions: 'retryStarted' },
          { guard: 'retryClose', target: 'closing', actions: 'retryStarted' },
        ],
        DISMISS_ERROR: [
          { guard: 'failedRestore', target: 'archived', actions: 'dismissError' },
          { guard: 'failedCloseFromArchive', target: 'archived', actions: 'dismissError' },
          { target: 'active', actions: 'dismissError' },
        ],
      },
    },
    closed: { type: 'final' },
  },
});

const createRevisionedMessagingMachine = (id: string) =>
  setup({
    types: {
      context: {} as {
        revision: number;
        serverRevision: number | null;
        error: string | null;
      },
      events: {} as
        | { type: 'SAVE' }
        | { type: 'SUCCEEDED'; revision: number }
        | { type: 'CONFLICT'; serverRevision: number }
        | { type: 'FAILED'; message: string }
        | { type: 'EDIT' },
    },
  }).createMachine({
    id,
    initial: 'ready',
    context: { revision: 0, serverRevision: null, error: null },
    states: {
      ready: { on: { SAVE: 'saving' } },
      saving: {
        on: {
          SUCCEEDED: {
            target: 'ready',
            actions: assign({
              revision: ({ event }) => event.revision,
              serverRevision: null,
              error: null,
            }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
        },
      },
      conflict: { on: { EDIT: 'ready' } },
      failed: { on: { EDIT: 'ready' } },
    },
  });

export const chatGroupMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      revisionGap: boolean;
      error: string | null;
      retryable: boolean;
      accessLoss: 'MEMBERSHIP_LOST' | 'BLOCKED' | null;
    },
    events: {} as
      | { type: 'HYDRATE'; revision: number; status: 'ACTIVE' | 'ARCHIVED' | 'CLOSED' }
      | { type: 'SAVE' }
      | { type: 'SUCCEEDED'; revision: number }
      | { type: 'CONFLICT'; serverRevision: number }
      | { type: 'FAILED'; message: string; retryable: boolean }
      | { type: 'RETRY' }
      | { type: 'EDIT' }
      | { type: 'REALTIME_REVISION'; revision: number }
      | { type: 'RECONCILED'; revision: number }
      | { type: 'MEMBERSHIP_LOST' }
      | { type: 'MEMBERSHIP_RESTORED' }
      | { type: 'BLOCK_CHANGED'; isBlocked: boolean }
      | { type: 'CLOSED'; revision: number },
  },
  guards: {
    hydratedClosed: ({ event }) => event.type === 'HYDRATE' && event.status === 'CLOSED',
    maySave: ({ context }) => context.accessLoss === null && !context.revisionGap,
    mayRetry: ({ context }) => context.retryable && context.accessLoss === null,
  },
  actions: {
    hydrate: assign({
      revision: ({ event }) => (event.type === 'HYDRATE' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      retryable: false,
    }),
    saveStarted: assign({ error: null, retryable: false }),
    saveSucceeded: assign({
      revision: ({ event }) => (event.type === 'SUCCEEDED' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      retryable: false,
    }),
    conflictDetected: assign({
      serverRevision: ({ event }) => (event.type === 'CONFLICT' ? event.serverRevision : null),
      revisionGap: true,
      error: 'A newer group revision must be reconciled before saving.',
      retryable: false,
    }),
    saveFailed: assign({
      error: ({ event }) => (event.type === 'FAILED' ? event.message : null),
      retryable: ({ event }) => event.type === 'FAILED' && event.retryable,
    }),
    realtimeRevision: assign({
      serverRevision: ({ context, event }) =>
        event.type === 'REALTIME_REVISION'
          ? Math.max(context.serverRevision ?? 0, event.revision)
          : context.serverRevision,
      revisionGap: ({ context, event }) =>
        event.type === 'REALTIME_REVISION' && event.revision > context.revision + 1,
      revision: ({ context, event }) =>
        event.type === 'REALTIME_REVISION' && event.revision === context.revision + 1
          ? event.revision
          : context.revision,
    }),
    reconciled: assign({
      revision: ({ event }) => (event.type === 'RECONCILED' ? event.revision : 0),
      serverRevision: null,
      revisionGap: false,
      error: null,
      retryable: false,
    }),
    membershipLost: assign({ accessLoss: 'MEMBERSHIP_LOST' }),
    membershipRestored: assign({
      accessLoss: ({ context }) =>
        context.accessLoss === 'MEMBERSHIP_LOST' ? null : context.accessLoss,
    }),
    blockChanged: assign({
      accessLoss: ({ context, event }) =>
        event.type === 'BLOCK_CHANGED'
          ? event.isBlocked
            ? 'BLOCKED'
            : context.accessLoss === 'BLOCKED'
              ? null
              : context.accessLoss
          : context.accessLoss,
    }),
    closed: assign({
      revision: ({ event }) => (event.type === 'CLOSED' ? event.revision : 0),
      error: null,
      retryable: false,
    }),
  },
}).createMachine({
  id: 'chatGroup',
  initial: 'ready',
  context: {
    revision: 0,
    serverRevision: null,
    revisionGap: false,
    error: null,
    retryable: false,
    accessLoss: null,
  },
  on: {
    REALTIME_REVISION: { actions: 'realtimeRevision' },
    RECONCILED: { target: '.ready', actions: 'reconciled' },
    MEMBERSHIP_LOST: { target: '.membershipLost', actions: 'membershipLost' },
    BLOCK_CHANGED: [
      { guard: ({ event }) => event.isBlocked, target: '.blocked', actions: 'blockChanged' },
      { actions: 'blockChanged' },
    ],
    CLOSED: { target: '.closed', actions: 'closed' },
  },
  states: {
    ready: {
      on: {
        HYDRATE: [
          { guard: 'hydratedClosed', target: 'closed', actions: 'hydrate' },
          { actions: 'hydrate' },
        ],
        SAVE: { guard: 'maySave', target: 'saving', actions: 'saveStarted' },
      },
    },
    saving: {
      on: {
        SUCCEEDED: { target: 'ready', actions: 'saveSucceeded' },
        CONFLICT: { target: 'conflict', actions: 'conflictDetected' },
        FAILED: { target: 'failed', actions: 'saveFailed' },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: {
      on: {
        RETRY: { guard: 'mayRetry', target: 'saving', actions: 'saveStarted' },
        EDIT: 'ready',
      },
    },
    membershipLost: {
      on: {
        MEMBERSHIP_RESTORED: { target: 'ready', actions: 'membershipRestored' },
      },
    },
    blocked: {
      on: {
        BLOCK_CHANGED: {
          guard: ({ event }) => !event.isBlocked,
          target: 'ready',
          actions: 'blockChanged',
        },
      },
    },
    closed: { type: 'final' },
  },
});
export const conversationMembershipMachine =
  createRevisionedMessagingMachine('conversationMembership');

export const messageSendMachine = setup({
  types: {
    context: {} as {
      clientMessageId: string | null;
      messageId: number | null;
      error: string | null;
    },
    events: {} as
      | { type: 'SEND'; clientMessageId: string }
      | { type: 'ACKNOWLEDGED'; messageId: number }
      | { type: 'OFFLINE' }
      | { type: 'RECONNECTED' }
      | { type: 'FAILED'; message: string }
      | { type: 'EDIT' },
  },
}).createMachine({
  id: 'messageSend',
  initial: 'composing',
  context: { clientMessageId: null, messageId: null, error: null },
  states: {
    composing: {
      on: {
        SEND: {
          target: 'sending',
          actions: assign({ clientMessageId: ({ event }) => event.clientMessageId, error: null }),
        },
      },
    },
    sending: {
      on: {
        ACKNOWLEDGED: {
          target: 'sent',
          actions: assign({ messageId: ({ event }) => event.messageId }),
        },
        OFFLINE: 'waitingForConnection',
        FAILED: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
      },
    },
    waitingForConnection: { on: { RECONNECTED: 'sending', EDIT: 'composing' } },
    sent: { on: { EDIT: 'composing' } },
    failed: { on: { EDIT: 'composing' } },
  },
});

export const messageReactionMachine = setup({
  types: {
    events: {} as { type: 'ADD' } | { type: 'ADDED' } | { type: 'REMOVE' } | { type: 'REMOVED' },
  },
}).createMachine({
  id: 'messageReaction',
  initial: 'inactive',
  states: {
    inactive: { on: { ADD: 'adding' } },
    adding: { on: { ADDED: 'active' } },
    active: { on: { REMOVE: 'removing' } },
    removing: { on: { REMOVED: 'inactive' } },
  },
});

export const moderationCaseMachine = setup({
  types: {
    events: {} as
      { type: 'MODERATE' } | { type: 'MODERATED' } | { type: 'REVERSE' } | { type: 'REVERSED' },
  },
}).createMachine({
  id: 'moderationCase',
  initial: 'visible',
  states: {
    visible: { on: { MODERATE: 'moderating' } },
    moderating: { on: { MODERATED: 'moderated' } },
    moderated: { on: { REVERSE: 'reversing' } },
    reversing: { on: { REVERSED: 'visible' } },
  },
});

export const assetProcessingMachine = setup({
  types: {
    events: {} as
      | { type: 'UPLOAD' }
      | { type: 'SCAN' }
      | { type: 'CLEAN' }
      | { type: 'REJECT' }
      | { type: 'EXPIRE' }
      | { type: 'MATERIALIZE' }
      | { type: 'MATERIALIZED' }
      | { type: 'AVAILABLE' }
      | { type: 'RETRY' }
      | { type: 'ARCHIVE' }
      | { type: 'ARCHIVED' }
      | { type: 'START_NEW_UPLOAD' }
      | { type: 'HYDRATE_PROCESSING' }
      | { type: 'HYDRATE_REJECTED' }
      | { type: 'HYDRATE_READY' }
      | { type: 'HYDRATE_UNAVAILABLE' }
      | { type: 'HYDRATE_REMOVED' },
  },
}).createMachine({
  id: 'assetProcessing',
  initial: 'intent',
  on: {
    START_NEW_UPLOAD: '.uploading',
    HYDRATE_PROCESSING: '.scanning',
    HYDRATE_REJECTED: '.rejected',
    HYDRATE_READY: '.materialized',
    HYDRATE_UNAVAILABLE: '.unavailable',
    HYDRATE_REMOVED: '.archived',
  },
  states: {
    intent: { on: { UPLOAD: 'uploading', EXPIRE: 'expired' } },
    uploading: {
      on: {
        SCAN: 'scanning',
        REJECT: 'rejected',
        EXPIRE: 'expired',
      },
    },
    scanning: {
      on: {
        CLEAN: 'clean',
        REJECT: 'rejected',
        EXPIRE: 'expired',
      },
    },
    clean: { on: { MATERIALIZE: 'materializing' } },
    materializing: {
      on: {
        MATERIALIZED: 'materialized',
        AVAILABLE: 'materialized',
        RETRY: 'retry',
        REJECT: 'rejected',
      },
    },
    rejected: { on: { RETRY: 'retry' } },
    expired: { on: { RETRY: 'retry' } },
    unavailable: { on: { RETRY: 'retry' } },
    retry: {
      on: {
        UPLOAD: 'uploading',
        SCAN: 'scanning',
        MATERIALIZE: 'materializing',
        EXPIRE: 'expired',
      },
    },
    materialized: { on: { ARCHIVE: 'archiving' } },
    archiving: { on: { ARCHIVED: 'archived', RETRY: 'materialized' } },
    archived: { type: 'final' },
  },
});

export const linkPreviewMachine = setup({
  types: {
    events: {} as
      | { type: 'REQUEST' }
      | { type: 'SAFE' }
      | { type: 'UNSAFE' }
      | { type: 'FAIL' }
      | { type: 'RETRY' },
  },
}).createMachine({
  id: 'linkPreview',
  initial: 'idle',
  states: {
    idle: { on: { REQUEST: 'checking' } },
    checking: { on: { SAFE: 'safe', UNSAFE: 'unsafe', FAIL: 'failed' } },
    safe: {},
    unsafe: {},
    failed: { on: { RETRY: 'checking' } },
  },
});

export const messageReceiptMachine = setup({
  types: { events: {} as { type: 'DELIVER' } | { type: 'READ' } },
}).createMachine({
  id: 'messageReceipt',
  initial: 'pending',
  states: {
    pending: { on: { DELIVER: 'delivered' } },
    delivered: { on: { READ: 'read' } },
    read: { type: 'final' },
  },
});

export const accountBlockMachine = setup({
  types: {
    events: {} as
      { type: 'BLOCK' } | { type: 'BLOCKED' } | { type: 'UNBLOCK' } | { type: 'UNBLOCKED' },
  },
}).createMachine({
  id: 'accountBlock',
  initial: 'unblocked',
  states: {
    unblocked: { on: { BLOCK: 'blocking' } },
    blocking: { on: { BLOCKED: 'blocked' } },
    blocked: { on: { UNBLOCK: 'unblocking' } },
    unblocking: { on: { UNBLOCKED: 'unblocked' } },
  },
});

export const safetyReportMachine = setup({
  types: {
    events: {} as
      | { type: 'SUBMIT' }
      | { type: 'SUBMITTED' }
      | { type: 'ASSIGN' }
      | { type: 'ASSIGNED' }
      | { type: 'RESOLVE' }
      | { type: 'RESOLVED' },
  },
}).createMachine({
  id: 'safetyReport',
  initial: 'draft',
  states: {
    draft: { on: { SUBMIT: 'submitting' } },
    submitting: { on: { SUBMITTED: 'open' } },
    open: { on: { ASSIGN: 'assigning' } },
    assigning: { on: { ASSIGNED: 'inReview' } },
    inReview: { on: { RESOLVE: 'resolving' } },
    resolving: { on: { RESOLVED: 'resolved' } },
    resolved: { type: 'final' },
  },
});

type EditorEvent =
  | { type: 'LOAD' }
  | { type: 'LOADED'; version: number }
  | { type: 'EDIT' }
  | { type: 'AUTOSAVE' }
  | { type: 'SAVED'; version: number }
  | { type: 'CONFLICT'; serverVersion: number }
  | { type: 'TAKE_OVER' }
  | { type: 'OFFLINE' }
  | { type: 'ONLINE' }
  | { type: 'VALID' }
  | { type: 'INVALID' }
  | { type: 'PUBLISH' }
  | { type: 'PUBLISHED' }
  | { type: 'FAIL'; message: string };

export const presentationTemplateMachine = setup({
  types: {
    events: {} as
      | { type: 'EDIT' }
      | { type: 'VALIDATE' }
      | { type: 'VALID' }
      | { type: 'INVALID' }
      | { type: 'PUBLISH' }
      | { type: 'PUBLISHED' }
      | { type: 'BRANCH' }
      | { type: 'BRANCHED' },
  },
}).createMachine({
  id: 'presentationTemplate',
  initial: 'draft',
  states: {
    draft: { on: { EDIT: 'editing', VALIDATE: 'validating', BRANCH: 'branching' } },
    editing: { on: { VALIDATE: 'validating', BRANCH: 'branching' } },
    validating: { on: { VALID: 'ready', INVALID: 'editing' } },
    ready: { on: { EDIT: 'editing', PUBLISH: 'publishing', BRANCH: 'branching' } },
    publishing: { on: { PUBLISHED: 'published', INVALID: 'editing' } },
    published: { on: { BRANCH: 'branching' } },
    branching: { on: { BRANCHED: 'draft', INVALID: 'published' } },
  },
});

export const layoutEditorMachine = setup({
  types: {
    events: {} as
      | { type: 'SELECT' }
      | { type: 'ADD' }
      | { type: 'MOVE' }
      | { type: 'REMOVE' }
      | { type: 'VALID' }
      | { type: 'CYCLE' },
  },
}).createMachine({
  id: 'layoutEditor',
  initial: 'idle',
  states: {
    idle: { on: { SELECT: 'selected', ADD: 'validating' } },
    selected: { on: { ADD: 'validating', MOVE: 'validating', REMOVE: 'validating' } },
    validating: { on: { VALID: 'selected', CYCLE: 'cycleRejected' } },
    cycleRejected: { on: { SELECT: 'selected' } },
  },
});

export const interactionAuthoringMachine = setup({
  types: {
    events: {} as
      | { type: 'EDIT' }
      | { type: 'ALIGN' }
      | { type: 'VALIDATE' }
      | { type: 'VALID' }
      | { type: 'INVALID' }
      | { type: 'SAVE' }
      | { type: 'SAVED' }
      | { type: 'CONFLICT' }
      | { type: 'PUBLISHED' }
      | { type: 'BRANCHED' }
      | { type: 'RESET' },
  },
}).createMachine({
  id: 'interactionAuthoring',
  initial: 'incomplete',
  states: {
    incomplete: {
      on: {
        EDIT: 'incomplete',
        ALIGN: 'incomplete',
        VALIDATE: 'incomplete',
        VALID: 'valid',
        INVALID: 'invalid',
        SAVE: 'saving',
        PUBLISHED: 'published',
        BRANCHED: 'branched',
      },
    },
    valid: {
      on: {
        EDIT: 'incomplete',
        ALIGN: 'incomplete',
        VALIDATE: 'valid',
        INVALID: 'invalid',
        SAVE: 'saving',
        PUBLISHED: 'published',
        BRANCHED: 'branched',
      },
    },
    invalid: {
      on: {
        EDIT: 'incomplete',
        ALIGN: 'incomplete',
        VALIDATE: 'invalid',
        VALID: 'valid',
        RESET: 'incomplete',
        BRANCHED: 'branched',
      },
    },
    saving: {
      on: {
        EDIT: 'incomplete',
        SAVED: 'valid',
        INVALID: 'invalid',
        CONFLICT: 'conflict',
        RESET: 'incomplete',
      },
    },
    conflict: {
      on: {
        EDIT: 'incomplete',
        RESET: 'incomplete',
        BRANCHED: 'branched',
      },
    },
    published: {
      on: {
        BRANCHED: 'branched',
      },
    },
    branched: {
      on: {
        EDIT: 'incomplete',
        ALIGN: 'incomplete',
        VALIDATE: 'branched',
        VALID: 'valid',
        INVALID: 'invalid',
        SAVE: 'saving',
        PUBLISHED: 'published',
      },
    },
  },
});

export const presentationEditorMachine = setup({
  types: {
    context: {} as {
      version: number;
      serverVersion: number | null;
      error: string | null;
    },
    events: {} as EditorEvent,
  },
}).createMachine({
  id: 'presentationEditor',
  type: 'parallel',
  context: { version: 0, serverVersion: null, error: null },
  states: {
    document: {
      initial: 'idle',
      states: {
        idle: { on: { LOAD: 'loading' } },
        loading: {
          on: {
            LOADED: {
              target: 'clean',
              actions: assign({ version: ({ event }) => event.version }),
            },
            FAIL: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
          },
        },
        clean: { on: { EDIT: 'dirty', PUBLISH: '#presentationEditor.publication.publishing' } },
        dirty: { on: { AUTOSAVE: 'saving', OFFLINE: 'offlineDirty' } },
        saving: {
          on: {
            SAVED: {
              target: 'clean',
              actions: assign({ version: ({ event }) => event.version }),
            },
            CONFLICT: {
              target: 'conflict',
              actions: assign({ serverVersion: ({ event }) => event.serverVersion }),
            },
            FAIL: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
          },
        },
        conflict: { on: { TAKE_OVER: 'dirty' } },
        offlineDirty: { on: { ONLINE: 'dirty' } },
        failed: { on: { AUTOSAVE: 'saving', EDIT: 'dirty' } },
      },
    },
    validation: {
      initial: 'unknown',
      states: {
        unknown: { on: { VALID: 'valid', INVALID: 'invalid' } },
        valid: { on: { EDIT: 'unknown', INVALID: 'invalid' } },
        invalid: { on: { EDIT: 'unknown', VALID: 'valid' } },
      },
    },
    publication: {
      initial: 'idle',
      states: {
        idle: { on: { PUBLISH: 'publishing' } },
        publishing: {
          on: {
            PUBLISHED: 'published',
            FAIL: { target: 'failed', actions: assign({ error: ({ event }) => event.message }) },
          },
        },
        published: { type: 'final' },
        failed: { on: { PUBLISH: 'publishing' } },
      },
    },
    connectivity: {
      initial: 'online',
      states: {
        online: { on: { OFFLINE: 'offline' } },
        offline: { on: { ONLINE: 'online' } },
      },
    },
  },
});

type MeetingEvent =
  | { type: 'AUTHORIZE'; role: 'HOST' | 'COHOST' | 'PARTICIPANT' }
  | { type: 'OPEN' }
  | { type: 'CONNECT_AUDIO' }
  | { type: 'AUDIO_CONNECTED' }
  | { type: 'AUDIO_FAILED' }
  | { type: 'DISCONNECT_AUDIO' }
  | { type: 'DISCONNECTED' }
  | { type: 'RECONNECTED' }
  | { type: 'REAUTHORIZED' }
  | { type: 'REAUTHORIZATION_FAILED' }
  | { type: 'REVISION'; revision: number }
  | { type: 'REVISION_GAP' }
  | { type: 'SNAPSHOT_RECOVERED'; revision: number }
  | { type: 'INTERACTION_OPENED'; interactionId: number }
  | { type: 'RESPONSE_SUBMITTED' }
  | { type: 'RESPONSE_ACKNOWLEDGED' }
  | { type: 'RECORDING_CONSENT_REQUIRED' }
  | { type: 'RECORDING_CONSENTED' }
  | { type: 'RECORDING_STARTED' }
  | { type: 'RECORDING_STOPPED' }
  | { type: 'CLOSE' }
  | { type: 'CLOSED' };

export const deliveryMachine = setup({
  types: {
    context: {} as { revision: number; error: string | null },
    events: {} as
      | { type: 'EDIT' }
      | { type: 'SAVE' }
      | { type: 'SAVED'; revision: number }
      | { type: 'LAUNCH' }
      | { type: 'LAUNCHED'; revision: number }
      | { type: 'CLOSE' }
      | { type: 'CLOSED'; revision: number }
      | { type: 'FAILED'; message: string }
      | { type: 'CONFLICT'; serverRevision: number },
  },
}).createMachine({
  id: 'delivery',
  initial: 'draft',
  context: { revision: 0, error: null },
  states: {
    draft: { on: { EDIT: 'editing', LAUNCH: 'launching' } },
    editing: { on: { SAVE: 'saving' } },
    saving: {
      on: {
        SAVED: {
          target: 'draft',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            revision: ({ event }) => event.serverRevision,
            error: 'The delivery changed in another session.',
          }),
        },
        FAILED: {
          target: 'editing',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'editing' } },
    launching: {
      on: {
        LAUNCHED: {
          target: 'active',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
        FAILED: {
          target: 'draft',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    active: { on: { CLOSE: 'closing' } },
    closing: {
      on: {
        CLOSED: {
          target: 'closed',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
        FAILED: {
          target: 'active',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    closed: { type: 'final' },
  },
});

export const selfPacedProgressMachine = setup({
  types: {
    context: {} as { percent: number; revision: number },
    events: {} as
      | { type: 'RESUME'; percent: number }
      | { type: 'ADVANCE'; percent: number }
      | { type: 'SYNCED'; revision: number }
      | { type: 'COMPLETE' },
  },
}).createMachine({
  id: 'selfPacedProgress',
  initial: 'idle',
  context: { percent: 0, revision: 0 },
  states: {
    idle: {
      on: {
        RESUME: {
          target: 'reading',
          actions: assign({ percent: ({ event }) => event.percent }),
        },
      },
    },
    reading: {
      on: {
        ADVANCE: {
          target: 'syncing',
          actions: assign({
            percent: ({ context, event }) => Math.max(context.percent, event.percent),
          }),
        },
        COMPLETE: 'completing',
      },
    },
    syncing: {
      on: {
        SYNCED: {
          target: 'reading',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
      },
    },
    completing: { on: { SYNCED: 'complete' } },
    complete: { type: 'final' },
  },
});

export const meetingAudioMachine = setup({
  types: {
    context: {} as { reconnects: number },
    events: {} as
      | { type: 'SELECT_DEVICE' }
      | { type: 'CONNECT' }
      | { type: 'CONNECTED' }
      | { type: 'DISCONNECT' }
      | { type: 'DISCONNECTED' }
      | { type: 'RECONNECTED' }
      | { type: 'PERMISSION_DENIED' }
      | { type: 'RETRY' },
  },
}).createMachine({
  id: 'meetingAudio',
  initial: 'prejoin',
  context: { reconnects: 0 },
  states: {
    prejoin: { on: { SELECT_DEVICE: 'ready', CONNECT: 'connecting' } },
    ready: { on: { CONNECT: 'connecting' } },
    connecting: { on: { CONNECTED: 'connected', PERMISSION_DENIED: 'permissionRecovery' } },
    connected: { on: { DISCONNECT: 'prejoin', DISCONNECTED: 'reconnecting' } },
    reconnecting: {
      entry: assign({ reconnects: ({ context }) => context.reconnects + 1 }),
      on: {
        DISCONNECT: 'prejoin',
        RECONNECTED: 'connected',
        PERMISSION_DENIED: 'permissionRecovery',
      },
    },
    permissionRecovery: { on: { RETRY: 'prejoin' } },
  },
});

export const meetingSynchronizationMachine = setup({
  types: {
    context: {} as {
      revision: number;
      pendingRevision: number | null;
      error: string | null;
    },
    events: {} as
      | { type: 'HYDRATE'; revision: number }
      | { type: 'ADVANCE' }
      | { type: 'ADVANCE_FAILED'; message: string }
      | { type: 'REVISION_APPLIED'; revision: number }
      | { type: 'GAP_DETECTED'; receivedRevision: number }
      | { type: 'RECOVER' }
      | { type: 'RECOVERED'; revision: number }
      | { type: 'RECOVERY_FAILED'; message: string }
      | { type: 'FORBIDDEN'; message: string }
      | { type: 'STALE'; currentRevision: number }
      | { type: 'DISCONNECTED' }
      | { type: 'RECONNECTED' }
      | { type: 'CLOSED' },
  },
}).createMachine({
  id: 'meetingSynchronization',
  initial: 'synchronized',
  context: { revision: 0, pendingRevision: null, error: null },
  states: {
    synchronized: {
      on: {
        HYDRATE: {
          actions: assign({
            revision: ({ event }) => event.revision,
            pendingRevision: null,
            error: null,
          }),
        },
        ADVANCE: { target: 'advancing', actions: assign({ error: null }) },
        REVISION_APPLIED: {
          actions: assign({
            revision: ({ context, event }) => Math.max(context.revision, event.revision),
            pendingRevision: null,
            error: null,
          }),
        },
        GAP_DETECTED: {
          target: 'gapDetected',
          actions: assign({
            pendingRevision: ({ event }) => event.receivedRevision,
            error: 'A live-position revision gap was detected.',
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        DISCONNECTED: 'disconnected',
        CLOSED: 'closed',
      },
    },
    advancing: {
      on: {
        REVISION_APPLIED: {
          target: 'synchronized',
          actions: assign({
            revision: ({ event }) => event.revision,
            pendingRevision: null,
            error: null,
          }),
        },
        ADVANCE_FAILED: {
          target: 'synchronized',
          actions: assign({ error: ({ event }) => event.message }),
        },
        STALE: {
          target: 'stale',
          actions: assign({
            pendingRevision: ({ event }) => event.currentRevision,
            error: 'This tab has a stale live-position revision.',
          }),
        },
        GAP_DETECTED: {
          target: 'gapDetected',
          actions: assign({
            pendingRevision: ({ event }) => event.receivedRevision,
            error: 'A live-position revision gap was detected.',
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        DISCONNECTED: 'disconnected',
        CLOSED: 'closed',
      },
    },
    gapDetected: {
      on: {
        RECOVER: 'recovering',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        DISCONNECTED: 'disconnected',
        CLOSED: 'closed',
      },
    },
    recovering: {
      on: {
        RECOVERED: {
          target: 'synchronized',
          actions: assign({
            revision: ({ event }) => event.revision,
            pendingRevision: null,
            error: null,
          }),
        },
        RECOVERY_FAILED: {
          target: 'gapDetected',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        DISCONNECTED: 'disconnected',
        CLOSED: 'closed',
      },
    },
    forbidden: {
      on: { CLOSED: 'closed' },
    },
    stale: {
      on: {
        RECOVER: 'recovering',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        DISCONNECTED: 'disconnected',
        CLOSED: 'closed',
      },
    },
    disconnected: {
      on: {
        RECONNECTED: 'recovering',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSED: 'closed',
      },
    },
    closed: { type: 'final' },
  },
});

export const interactionResponseMachine = setup({
  types: {
    context: {} as {
      interactionId: number | null;
      revision: number;
      error: string | null;
    },
    events: {} as
      | { type: 'OPEN'; interactionId: number }
      | { type: 'OPENED' }
      | { type: 'ANSWER' }
      | { type: 'SUBMIT' }
      | { type: 'ACKNOWLEDGED'; revision: number }
      | { type: 'REVISE' }
      | { type: 'REVISION_READY' }
      | { type: 'RESULTS_HIDDEN' }
      | { type: 'AGGREGATE_RESULTS' }
      | { type: 'NAMED_RESULTS' }
      | { type: 'STALE'; currentRevision: number }
      | { type: 'RETRY' }
      | { type: 'FORBIDDEN'; message: string }
      | { type: 'CLOSE' },
  },
}).createMachine({
  id: 'interactionResponse',
  initial: 'waiting',
  context: { interactionId: null, revision: 0, error: null },
  states: {
    waiting: {
      on: {
        OPEN: {
          target: 'opening',
          actions: assign({
            interactionId: ({ event }) => event.interactionId,
            revision: 0,
            error: null,
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    opening: {
      on: {
        OPENED: 'responding',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    responding: {
      on: {
        ANSWER: 'responding',
        SUBMIT: 'submitting',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    submitting: {
      on: {
        ACKNOWLEDGED: {
          target: 'acknowledged',
          actions: assign({
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        STALE: {
          target: 'stale',
          actions: assign({
            revision: ({ event }) => event.currentRevision,
            error: () => 'The response changed in another session.',
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    acknowledged: {
      on: {
        REVISE: 'revising',
        RESULTS_HIDDEN: 'resultsHidden',
        AGGREGATE_RESULTS: 'aggregateResults',
        NAMED_RESULTS: 'namedResults',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    revising: {
      on: {
        REVISION_READY: 'responding',
        SUBMIT: 'submitting',
        STALE: {
          target: 'stale',
          actions: assign({
            revision: ({ event }) => event.currentRevision,
            error: () => 'The response changed in another session.',
          }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    resultsHidden: {
      on: {
        REVISE: 'revising',
        AGGREGATE_RESULTS: 'aggregateResults',
        NAMED_RESULTS: 'namedResults',
        CLOSE: 'closed',
      },
    },
    aggregateResults: {
      on: {
        REVISE: 'revising',
        RESULTS_HIDDEN: 'resultsHidden',
        NAMED_RESULTS: 'namedResults',
        CLOSE: 'closed',
      },
    },
    namedResults: {
      on: {
        REVISE: 'revising',
        RESULTS_HIDDEN: 'resultsHidden',
        AGGREGATE_RESULTS: 'aggregateResults',
        CLOSE: 'closed',
      },
    },
    stale: {
      on: {
        RETRY: 'revising',
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CLOSE: 'closed',
      },
    },
    forbidden: { on: { CLOSE: 'closed' } },
    closed: { type: 'final' },
  },
});

export const recordingMachine = setup({
  types: {
    events: {} as
      | { type: 'HYDRATE'; status: RecordingLifecycle }
      | { type: 'REQUEST' }
      | { type: 'CONSENT_READY' }
      | { type: 'STARTED' }
      | { type: 'STOP' }
      | { type: 'PROCESS' }
      | { type: 'RECONCILE' }
      | { type: 'PARTIAL' }
      | { type: 'AVAILABLE' }
      | { type: 'FAIL' }
      | { type: 'RETRY' }
      | { type: 'ACCESS_EXPIRED' }
      | { type: 'ARCHIVE' },
  },
}).createMachine({
  id: 'recording',
  initial: 'idle',
  on: {
    ACCESS_EXPIRED: '.accessExpired',
    HYDRATE: [
      { guard: ({ event }) => event.status === 'IDLE', target: '.idle' },
      {
        guard: ({ event }) => event.status === 'AWAITING_CONSENT',
        target: '.awaitingConsent',
      },
      { guard: ({ event }) => event.status === 'STARTING', target: '.starting' },
      { guard: ({ event }) => event.status === 'RECORDING', target: '.recording' },
      { guard: ({ event }) => event.status === 'STOPPING', target: '.stopping' },
      { guard: ({ event }) => event.status === 'PROCESSING', target: '.processing' },
      {
        guard: ({ event }) => event.status === 'PARTIALLY_AVAILABLE',
        target: '.partiallyAvailable',
      },
      { guard: ({ event }) => event.status === 'AVAILABLE', target: '.available' },
      { guard: ({ event }) => event.status === 'FAILED', target: '.failed' },
      { guard: ({ event }) => event.status === 'RETRYING', target: '.retrying' },
      { guard: ({ event }) => event.status === 'ARCHIVED', target: '.archived' },
    ],
  },
  states: {
    idle: { on: { REQUEST: 'awaitingConsent' } },
    awaitingConsent: { on: { CONSENT_READY: 'starting', ARCHIVE: 'archived' } },
    starting: { on: { STARTED: 'recording', FAIL: 'failed', ARCHIVE: 'archived' } },
    recording: { on: { STOP: 'stopping' } },
    stopping: { on: { PROCESS: 'processing', FAIL: 'failed' } },
    processing: {
      on: {
        REQUEST: 'awaitingConsent',
        RECONCILE: 'reconciling',
        PARTIAL: 'partiallyAvailable',
        AVAILABLE: 'available',
        FAIL: 'failed',
      },
    },
    reconciling: {
      on: {
        PARTIAL: 'partiallyAvailable',
        AVAILABLE: 'available',
        FAIL: 'failed',
      },
    },
    partiallyAvailable: {
      on: {
        REQUEST: 'awaitingConsent',
        AVAILABLE: 'available',
        FAIL: 'failed',
        RETRY: 'retrying',
        ARCHIVE: 'archived',
      },
    },
    failed: {
      on: {
        REQUEST: 'awaitingConsent',
        RETRY: 'retrying',
        ARCHIVE: 'archived',
      },
    },
    retrying: {
      on: {
        PROCESS: 'processing',
        RECONCILE: 'reconciling',
        PARTIAL: 'partiallyAvailable',
        FAIL: 'failed',
      },
    },
    available: { on: { REQUEST: 'awaitingConsent', ARCHIVE: 'archived' } },
    accessExpired: {},
    archived: { type: 'final' },
  },
});

type RecordingOutputLifecycle =
  'PENDING' | 'PROCESSING' | 'PARTIALLY_AVAILABLE' | 'AVAILABLE' | 'FAILED' | 'DELETED';

export const recordingOutputMachine = setup({
  types: {
    events: {} as
      | { type: 'HYDRATE'; status: RecordingOutputLifecycle }
      | { type: 'PROCESS' }
      | { type: 'RECONCILE' }
      | { type: 'PARTIAL' }
      | { type: 'READY' }
      | { type: 'SIGN' }
      | { type: 'SIGNED' }
      | { type: 'EXPIRE' }
      | { type: 'ACCESS_EXPIRED' }
      | { type: 'RESTORE_ACCESS' }
      | { type: 'FAIL' }
      | { type: 'RETRY' }
      | { type: 'DELETE' },
  },
}).createMachine({
  id: 'recordingOutput',
  initial: 'pending',
  on: {
    ACCESS_EXPIRED: '.accessExpired',
    HYDRATE: [
      { guard: ({ event }) => event.status === 'PENDING', target: '.pending' },
      { guard: ({ event }) => event.status === 'PROCESSING', target: '.processing' },
      {
        guard: ({ event }) => event.status === 'PARTIALLY_AVAILABLE',
        target: '.partiallyAvailable',
      },
      { guard: ({ event }) => event.status === 'AVAILABLE', target: '.available' },
      { guard: ({ event }) => event.status === 'FAILED', target: '.failed' },
      { guard: ({ event }) => event.status === 'DELETED', target: '.deleted' },
    ],
  },
  states: {
    pending: { on: { PROCESS: 'processing', READY: 'available', FAIL: 'failed' } },
    processing: {
      on: {
        RECONCILE: 'reconciling',
        PARTIAL: 'partiallyAvailable',
        READY: 'available',
        FAIL: 'failed',
      },
    },
    reconciling: {
      on: {
        PARTIAL: 'partiallyAvailable',
        READY: 'available',
        FAIL: 'failed',
      },
    },
    partiallyAvailable: {
      on: {
        RECONCILE: 'reconciling',
        READY: 'available',
        RETRY: 'processing',
        FAIL: 'failed',
        DELETE: 'deleted',
      },
    },
    available: { on: { SIGN: 'signing' } },
    signing: {
      on: {
        SIGNED: 'playable',
        FAIL: 'failed',
        ACCESS_EXPIRED: 'accessExpired',
      },
    },
    playable: { on: { EXPIRE: 'available' } },
    accessExpired: {
      on: {
        RESTORE_ACCESS: 'available',
        DELETE: 'deleted',
      },
    },
    failed: { on: { RETRY: 'processing', DELETE: 'deleted' } },
    deleted: { type: 'final' },
  },
});

export const meetingMachine = setup({
  types: {
    context: {} as {
      role: 'HOST' | 'COHOST' | 'PARTICIPANT' | null;
      revision: number;
      interactionId: number | null;
      recording: RecordingLifecycle;
    },
    events: {} as MeetingEvent,
  },
}).createMachine({
  id: 'meeting',
  initial: 'authorizing',
  context: { role: null, revision: 0, interactionId: null, recording: 'IDLE' },
  states: {
    authorizing: {
      on: {
        AUTHORIZE: {
          target: 'lobby',
          actions: assign({ role: ({ event }) => event.role }),
        },
      },
    },
    lobby: { on: { OPEN: 'live' } },
    live: {
      type: 'parallel',
      on: { CLOSE: 'closing' },
      states: {
        audio: {
          initial: 'idle',
          states: {
            idle: { on: { CONNECT_AUDIO: 'connecting' } },
            connecting: { on: { AUDIO_CONNECTED: 'connected', AUDIO_FAILED: 'failed' } },
            connected: {
              on: { DISCONNECT_AUDIO: 'idle', DISCONNECTED: 'reconnecting' },
            },
            reconnecting: {
              on: {
                DISCONNECT_AUDIO: 'idle',
                RECONNECTED: 'reauthorizing',
                AUDIO_FAILED: 'failed',
              },
            },
            reauthorizing: {
              on: {
                REAUTHORIZED: 'connected',
                REAUTHORIZATION_FAILED: 'failed',
                DISCONNECT_AUDIO: 'idle',
              },
            },
            failed: {
              on: { CONNECT_AUDIO: 'connecting', DISCONNECT_AUDIO: 'idle' },
            },
          },
        },
        synchronization: {
          initial: 'current',
          states: {
            current: {
              on: {
                REVISION: {
                  actions: assign({
                    revision: ({ context, event }) => Math.max(context.revision, event.revision),
                  }),
                },
                REVISION_GAP: 'recovering',
              },
            },
            recovering: {
              on: {
                SNAPSHOT_RECOVERED: {
                  target: 'current',
                  actions: assign({ revision: ({ event }) => event.revision }),
                },
              },
            },
          },
        },
        interaction: {
          initial: 'idle',
          states: {
            idle: {
              on: {
                INTERACTION_OPENED: {
                  target: 'answering',
                  actions: assign({ interactionId: ({ event }) => event.interactionId }),
                },
              },
            },
            answering: { on: { RESPONSE_SUBMITTED: 'submitting' } },
            submitting: { on: { RESPONSE_ACKNOWLEDGED: 'acknowledged' } },
            acknowledged: { on: { INTERACTION_OPENED: 'answering' } },
          },
        },
        recording: {
          initial: 'idle',
          states: {
            idle: {
              on: {
                RECORDING_CONSENT_REQUIRED: {
                  target: 'awaitingConsent',
                  actions: assign({ recording: 'AWAITING_CONSENT' }),
                },
                RECORDING_STARTED: {
                  target: 'active',
                  actions: assign({ recording: 'RECORDING' }),
                },
              },
            },
            awaitingConsent: {
              on: {
                RECORDING_CONSENTED: {
                  target: 'starting',
                  actions: assign({ recording: 'STARTING' }),
                },
              },
            },
            starting: {
              on: {
                RECORDING_STARTED: {
                  target: 'active',
                  actions: assign({ recording: 'RECORDING' }),
                },
              },
            },
            active: {
              on: {
                RECORDING_STOPPED: {
                  target: 'idle',
                  actions: assign({ recording: 'IDLE' }),
                },
              },
            },
          },
        },
        attendance: {
          initial: 'heartbeating',
          states: {
            heartbeating: { on: { DISCONNECTED: 'paused' } },
            paused: { on: { RECONNECTED: 'heartbeating' } },
          },
        },
      },
    },
    closing: { on: { CLOSED: 'closed' } },
    closed: { type: 'final' },
  },
});

type ScheduleMaterializationEvent =
  | { type: 'EDIT' }
  | { type: 'PREVIEW'; conflictCount: number }
  | { type: 'SAVE' }
  | { type: 'MATERIALIZE' }
  | { type: 'SUCCEEDED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'RETRY' };

export const scheduleMaterializationMachine = setup({
  types: {
    context: {} as {
      revision: number;
      conflictCount: number;
      serverRevision: number | null;
      operation: 'SAVE' | 'MATERIALIZE' | null;
      error: string | null;
    },
    events: {} as ScheduleMaterializationEvent,
  },
  guards: {
    hasNoConflicts: ({ context }) => context.conflictCount === 0,
    wasMaterializing: ({ context }) => context.operation === 'MATERIALIZE',
  },
}).createMachine({
  id: 'scheduleMaterialization',
  initial: 'editing',
  context: {
    revision: 0,
    conflictCount: 0,
    serverRevision: null,
    operation: null,
    error: null,
  },
  states: {
    editing: {
      on: {
        PREVIEW: {
          target: 'previewed',
          actions: assign({ conflictCount: ({ event }) => event.conflictCount }),
        },
        SAVE: {
          target: 'saving',
          actions: assign({ operation: 'SAVE', error: null }),
        },
      },
    },
    previewed: {
      on: {
        EDIT: 'editing',
        SAVE: {
          target: 'saving',
          actions: assign({ operation: 'SAVE', error: null }),
        },
        MATERIALIZE: {
          guard: 'hasNoConflicts',
          target: 'materializing',
          actions: assign({ operation: 'MATERIALIZE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'saved',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: { on: { MATERIALIZE: 'materializing', EDIT: 'editing' } },
    materializing: {
      on: {
        SUCCEEDED: {
          target: 'materialized',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    materialized: { on: { EDIT: 'editing' } },
    conflict: { on: { EDIT: 'editing' } },
    failed: {
      on: {
        RETRY: [
          {
            guard: 'wasMaterializing',
            target: 'materializing',
            actions: assign({ error: null }),
          },
          { target: 'saving', actions: assign({ error: null }) },
        ],
        EDIT: 'editing',
      },
    },
  },
});

export const classSessionMachine = setup({
  types: {
    context: {} as { revision: number; serverRevision: number | null; error: string | null },
    events: {} as
      | { type: 'HYDRATE'; status: string; revision: number }
      | { type: 'SAVE' }
      | { type: 'CANCEL' }
      | { type: 'COMPLETE' }
      | { type: 'SUCCEEDED'; status: string; revision: number }
      | { type: 'CONFLICT'; serverRevision: number }
      | { type: 'FAILED'; message: string }
      | { type: 'EDIT' },
  },
}).createMachine({
  id: 'classSession',
  initial: 'loading',
  context: { revision: 0, serverRevision: null, error: null },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({ revision: ({ event }) => event.revision }),
        },
      },
    },
    ready: { on: { SAVE: 'saving', CANCEL: 'saving', COMPLETE: 'saving' } },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({ revision: ({ event }) => event.revision, error: null }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

export const groupTaskMachine = setup({
  types: {
    context: {} as {
      revision: number;
      serverRevision: number | null;
      status: string;
      error: string | null;
    },
    events: {} as
      | { type: 'HYDRATE'; status: string; revision: number }
      | { type: 'SAVE' }
      | { type: 'PUBLISH' }
      | { type: 'CANCEL' }
      | { type: 'SUCCEEDED'; status: string; revision: number }
      | { type: 'CONFLICT'; serverRevision: number }
      | { type: 'FAILED'; message: string }
      | { type: 'EDIT' },
  },
}).createMachine({
  id: 'groupTask',
  initial: 'loading',
  context: { revision: 0, serverRevision: null, status: 'DRAFT', error: null },
  states: {
    loading: {
      on: {
        HYDRATE: {
          target: 'ready',
          actions: assign({
            revision: ({ event }) => event.revision,
            status: ({ event }) => event.status,
          }),
        },
      },
    },
    ready: { on: { SAVE: 'saving', PUBLISH: 'publishing', CANCEL: 'cancelling' } },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            revision: ({ event }) => event.revision,
            status: ({ event }) => event.status,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    publishing: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            revision: ({ event }) => event.revision,
            status: ({ event }) => event.status,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    cancelling: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            revision: ({ event }) => event.revision,
            status: ({ event }) => event.status,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { EDIT: 'ready' } },
  },
});

type AssessmentFailureEvent =
  { type: 'FAILED'; message: string } | { type: 'CONFLICT'; serverRevision: number };

export const taskAttemptMachine = setup({
  types: {
    context: {} as {
      taskStatus: string;
      attemptStatus: string | null;
      revision: number;
      canGrade: boolean;
      error: string | null;
    },
    events: {} as
      | {
          type: 'HYDRATE';
          taskStatus: string;
          attemptStatus: string | null;
          revision: number;
          canGrade?: boolean;
        }
      | { type: 'START_TASK' }
      | { type: 'START_ATTEMPT' }
      | { type: 'SUBMIT' }
      | { type: 'LINK_EVIDENCE' }
      | { type: 'RESUBMIT' }
      | { type: 'SELECT_OFFICIAL' }
      | { type: 'COMPLETE' }
      | {
          type: 'SUCCEEDED';
          taskStatus: string;
          attemptStatus: string | null;
          revision: number;
        }
      | AssessmentFailureEvent
      | { type: 'RETRY' },
  },
  guards: {
    canStartTask: ({ context }) => context.taskStatus === 'ASSIGNED',
    canStartAttempt: ({ context }) =>
      ['ASSIGNED', 'IN_PROGRESS'].includes(context.taskStatus) && context.attemptStatus === null,
    canSubmit: ({ context }) =>
      context.attemptStatus === 'DRAFT' || context.attemptStatus === 'IN_PROGRESS',
    canLinkEvidence: ({ context }) =>
      ['DRAFT', 'IN_PROGRESS'].includes(context.attemptStatus ?? '') ||
      (context.canGrade && ['SUBMITTED', 'GRADED'].includes(context.attemptStatus ?? '')),
    canResubmit: ({ context }) =>
      ['SUBMITTED', 'COMPLETED'].includes(context.taskStatus) &&
      ['SUBMITTED', 'GRADED'].includes(context.attemptStatus ?? ''),
    canSelectOfficial: ({ context }) => context.attemptStatus === 'GRADED',
    canComplete: ({ context }) => ['IN_PROGRESS', 'SUBMITTED'].includes(context.taskStatus),
  },
}).createMachine({
  id: 'taskAttempt',
  initial: 'ready',
  context: {
    taskStatus: 'ASSIGNED',
    attemptStatus: null,
    revision: 0,
    canGrade: false,
    error: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            taskStatus: ({ event }) => event.taskStatus,
            attemptStatus: ({ event }) => event.attemptStatus,
            revision: ({ event }) => event.revision,
            canGrade: ({ event }) => event.canGrade ?? false,
            error: null,
          }),
        },
        START_TASK: { guard: 'canStartTask', target: 'saving' },
        START_ATTEMPT: { guard: 'canStartAttempt', target: 'saving' },
        SUBMIT: { guard: 'canSubmit', target: 'saving' },
        LINK_EVIDENCE: { guard: 'canLinkEvidence', target: 'saving' },
        RESUBMIT: { guard: 'canResubmit', target: 'saving' },
        SELECT_OFFICIAL: { guard: 'canSelectOfficial', target: 'saving' },
        COMPLETE: { guard: 'canComplete', target: 'saving' },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            taskStatus: ({ event }) => event.taskStatus,
            attemptStatus: ({ event }) => event.attemptStatus,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            revision: ({ event }) => event.serverRevision,
            error: 'The task changed in another session.',
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { RETRY: 'ready' } },
    failed: { on: { RETRY: 'ready' } },
  },
});

export const attemptGradingMachine = setup({
  types: {
    context: {} as { attemptStatus: string; revision: number; error: string | null },
    events: {} as
      | { type: 'HYDRATE'; attemptStatus: string; revision: number }
      | { type: 'SCORE' }
      | { type: 'REVISE' }
      | { type: 'FINALIZE' }
      | { type: 'SUCCEEDED'; attemptStatus: string; revision: number }
      | AssessmentFailureEvent
      | { type: 'RETRY' },
  },
  guards: {
    canScore: ({ context }) => context.attemptStatus === 'SUBMITTED',
    canRevise: ({ context }) =>
      context.attemptStatus === 'SUBMITTED' || context.attemptStatus === 'GRADED',
    canFinalize: ({ context }) => context.attemptStatus === 'SUBMITTED',
  },
}).createMachine({
  id: 'attemptGrading',
  initial: 'ready',
  context: { attemptStatus: 'SUBMITTED', revision: 0, error: null },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            attemptStatus: ({ event }) => event.attemptStatus,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        SCORE: { guard: 'canScore', target: 'saving' },
        REVISE: { guard: 'canRevise', target: 'saving' },
        FINALIZE: { guard: 'canFinalize', target: 'saving' },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            attemptStatus: ({ event }) => event.attemptStatus,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            revision: ({ event }) => event.serverRevision,
            error: 'The grading record changed.',
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { RETRY: 'ready' } },
    failed: { on: { RETRY: 'ready' } },
  },
});

export const attendanceMachine = setup({
  types: {
    context: {} as {
      status: string;
      revision: number;
      canManage: boolean;
      error: string | null;
    },
    events: {} as
      | { type: 'HYDRATE'; status: string; revision: number; canManage: boolean }
      | { type: 'CONFIRM' }
      | { type: 'CORRECT' }
      | { type: 'SUCCEEDED'; status: string; revision: number }
      | AssessmentFailureEvent
      | { type: 'RETRY' },
  },
  guards: {
    canConfirm: ({ context }) => context.canManage && context.status === 'UNKNOWN',
    canCorrect: ({ context }) =>
      context.canManage &&
      ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED', 'CORRECTED'].includes(context.status),
  },
}).createMachine({
  id: 'attendance',
  initial: 'ready',
  context: { status: 'UNKNOWN', revision: 0, canManage: false, error: null },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            canManage: ({ event }) => event.canManage,
            error: null,
          }),
        },
        CONFIRM: { guard: 'canConfirm', target: 'saving' },
        CORRECT: { guard: 'canCorrect', target: 'saving' },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            revision: ({ event }) => event.serverRevision,
            error: 'Attendance changed in another register.',
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: { on: { RETRY: 'ready' } },
    failed: { on: { RETRY: 'ready' } },
  },
});

export const skillProjectionMachine = setup({
  types: {
    context: {} as { stale: boolean; calculatedAt: string | null; error: string | null },
    events: {} as
      | { type: 'HYDRATE'; stale: boolean; calculatedAt: string | null }
      | { type: 'REFRESH' }
      | { type: 'SUCCEEDED'; stale: boolean; calculatedAt: string | null }
      | { type: 'FAILED'; message: string }
      | { type: 'RETRY' },
  },
}).createMachine({
  id: 'skillProjection',
  initial: 'ready',
  context: { stale: false, calculatedAt: null, error: null },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            stale: ({ event }) => event.stale,
            calculatedAt: ({ event }) => event.calculatedAt,
            error: null,
          }),
        },
        REFRESH: 'refreshing',
      },
    },
    refreshing: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            stale: ({ event }) => event.stale,
            calculatedAt: ({ event }) => event.calculatedAt,
            error: null,
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    failed: { on: { RETRY: 'refreshing' } },
  },
});

export const enrollmentOutcomeMachine = setup({
  types: {
    context: {} as {
      outcome: string | null;
      revision: number;
      error: string | null;
      pendingOperation: 'DECIDE' | 'REVISE' | null;
    },
    events: {} as
      | { type: 'HYDRATE'; outcome: string | null; revision: number }
      | { type: 'DECIDE' }
      | { type: 'REVISE' }
      | { type: 'SUCCEEDED'; outcome: string; revision: number }
      | AssessmentFailureEvent
      | { type: 'RETRY' }
      | { type: 'RELOAD' },
  },
  guards: {
    canDecide: ({ context }) => context.outcome === null,
    canRevise: ({ context }) => context.outcome !== null,
  },
}).createMachine({
  id: 'enrollmentOutcome',
  initial: 'ready',
  context: { outcome: null, revision: 0, error: null, pendingOperation: null },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            outcome: ({ event }) => event.outcome,
            revision: ({ event }) => event.revision,
            error: null,
            pendingOperation: null,
          }),
        },
        DECIDE: {
          guard: 'canDecide',
          target: 'saving',
          actions: assign({ pendingOperation: 'DECIDE', error: null }),
        },
        REVISE: {
          guard: 'canRevise',
          target: 'saving',
          actions: assign({ pendingOperation: 'REVISE', error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'ready',
          actions: assign({
            outcome: ({ event }) => event.outcome,
            revision: ({ event }) => event.revision,
            error: null,
            pendingOperation: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({
            revision: ({ event }) => event.serverRevision,
            error: 'The enrollment decision changed.',
          }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    conflict: {
      on: {
        RELOAD: { target: 'ready', actions: assign({ error: null }) },
      },
    },
    failed: { on: { RETRY: 'saving' } },
  },
});

export const auditExplorerMachine = setup({
  types: {
    context: {} as {
      selectedEventId: string | null;
      exporting: boolean;
      error: string | null;
    },
    events: {} as
      | { type: 'SELECT'; eventId: string }
      | { type: 'CLEAR_SELECTION' }
      | { type: 'LOAD_PAGE' }
      | { type: 'PAGE_LOADED' }
      | { type: 'PAGE_FAILED'; message: string }
      | { type: 'FORBIDDEN'; message: string }
      | { type: 'RETRY_PAGE' }
      | { type: 'RESET' }
      | { type: 'EXPORT' }
      | { type: 'EXPORT_SUCCEEDED' }
      | { type: 'FAILED'; message: string }
      | { type: 'RETRY' },
  },
}).createMachine({
  id: 'auditExplorer',
  initial: 'ready',
  context: { selectedEventId: null, exporting: false, error: null },
  states: {
    ready: {
      on: {
        SELECT: {
          actions: assign({ selectedEventId: ({ event }) => event.eventId, error: null }),
        },
        CLEAR_SELECTION: {
          actions: assign({ selectedEventId: null, error: null }),
        },
        LOAD_PAGE: {
          target: 'loadingPage',
          actions: assign({ error: null }),
        },
        EXPORT: {
          target: 'exporting',
          actions: assign({ exporting: true, error: null }),
        },
      },
    },
    loadingPage: {
      on: {
        PAGE_LOADED: { target: 'ready', actions: assign({ error: null }) },
        PAGE_FAILED: {
          target: 'pageFailed',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    pageFailed: {
      on: {
        RETRY_PAGE: { target: 'loadingPage', actions: assign({ error: null }) },
        RESET: { target: 'ready', actions: assign({ error: null }) },
      },
    },
    forbidden: {
      on: { RESET: { target: 'ready', actions: assign({ error: null }) } },
    },
    exporting: {
      on: {
        EXPORT_SUCCEEDED: {
          target: 'ready',
          actions: assign({ exporting: false, error: null }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ exporting: false, error: ({ event }) => event.message }),
        },
      },
    },
    failed: { on: { RETRY: 'ready' } },
  },
});

export const ingestKeyMachine = setup({
  types: {
    context: {} as {
      status: 'ACTIVE' | 'REVOKED' | 'ROTATED' | 'EXPIRED';
      revision: number;
      intent: 'REVOKE' | 'ROTATE' | null;
      error: string | null;
      currentRevision: number | null;
    },
    events: {} as
      | {
          type: 'HYDRATE';
          status: 'ACTIVE' | 'REVOKED' | 'ROTATED' | 'EXPIRED';
          revision: number;
        }
      | { type: 'REVOKE' }
      | { type: 'ROTATE' }
      | { type: 'REAUTHENTICATED' }
      | { type: 'CONFIRM' }
      | { type: 'CANCEL' }
      | { type: 'SUCCEEDED'; status: 'REVOKED' | 'ROTATED'; revision: number }
      | { type: 'CONFLICT'; currentRevision: number | null }
      | { type: 'FAILED'; message: string }
      | { type: 'RETRY' }
      | { type: 'ACKNOWLEDGE' }
      | { type: 'RESET' },
  },
  guards: {
    isActive: ({ context }) => context.status === 'ACTIVE',
    isRotatedResult: ({ event }) => event.type === 'SUCCEEDED' && event.status === 'ROTATED',
  },
}).createMachine({
  id: 'ingestKey',
  initial: 'ready',
  context: {
    status: 'ACTIVE',
    revision: 0,
    intent: null,
    error: null,
    currentRevision: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            intent: null,
            error: null,
            currentRevision: null,
          }),
        },
        REVOKE: {
          guard: 'isActive',
          target: 'reauthenticating',
          actions: assign({ intent: () => 'REVOKE' as const, error: null }),
        },
        ROTATE: {
          guard: 'isActive',
          target: 'reauthenticating',
          actions: assign({ intent: () => 'ROTATE' as const, error: null }),
        },
      },
    },
    reauthenticating: {
      on: {
        REAUTHENTICATED: 'confirming',
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
        CANCEL: {
          target: 'ready',
          actions: assign({ intent: null, error: null }),
        },
      },
    },
    confirming: {
      on: {
        CONFIRM: 'saving',
        CANCEL: {
          target: 'ready',
          actions: assign({ intent: null, error: null }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: [
          {
            guard: 'isRotatedResult',
            target: 'rotated',
            actions: assign({
              status: () => 'ROTATED' as const,
              revision: ({ event }) => event.revision,
              error: null,
            }),
          },
          {
            target: 'revoked',
            actions: assign({
              status: () => 'REVOKED' as const,
              revision: ({ event }) => event.revision,
              error: null,
            }),
          },
        ],
        CONFLICT: {
          target: 'conflict',
          actions: assign({ currentRevision: ({ event }) => event.currentRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    rotated: {
      on: {
        ACKNOWLEDGE: {
          target: 'ready',
          actions: assign({ intent: null, error: null }),
        },
      },
    },
    revoked: {
      on: {
        ACKNOWLEDGE: {
          target: 'ready',
          actions: assign({ intent: null, error: null }),
        },
      },
    },
    conflict: {
      on: {
        RETRY: { target: 'reauthenticating', actions: assign({ error: null }) },
        RESET: {
          target: 'ready',
          actions: assign({ intent: null, error: null, currentRevision: null }),
        },
      },
    },
    failed: {
      on: {
        RETRY: { target: 'reauthenticating', actions: assign({ error: null }) },
        RESET: {
          target: 'ready',
          actions: assign({ intent: null, error: null }),
        },
      },
    },
  },
});

export const integrityCheckpointMachine = setup({
  types: {
    context: {} as {
      operation: 'GENERATE' | 'VERIFY' | 'EXPORT' | null;
      verification: 'UNKNOWN' | 'VERIFIED' | 'MISMATCH';
      exported: boolean;
      error: string | null;
    },
    events: {} as
      | { type: 'GENERATE' }
      | { type: 'VERIFY' }
      | { type: 'EXPORT' }
      | { type: 'GENERATED' }
      | { type: 'VERIFIED'; matches: boolean }
      | { type: 'EXPORTED' }
      | { type: 'FAILED'; message: string }
      | { type: 'FORBIDDEN'; message: string }
      | { type: 'RETRY' }
      | { type: 'ACKNOWLEDGE' },
  },
  guards: {
    verificationMatches: ({ event }) => event.type === 'VERIFIED' && event.matches,
    wasGenerating: ({ context }) => context.operation === 'GENERATE',
    wasVerifying: ({ context }) => context.operation === 'VERIFY',
    wasExporting: ({ context }) => context.operation === 'EXPORT',
  },
  actions: {
    clearOperation: assign({ operation: null, error: null }),
  },
}).createMachine({
  id: 'integrityCheckpoint',
  initial: 'ready',
  context: {
    operation: null,
    verification: 'UNKNOWN',
    exported: false,
    error: null,
  },
  states: {
    ready: {
      on: {
        GENERATE: {
          target: 'generating',
          actions: assign({ operation: 'GENERATE', error: null }),
        },
        VERIFY: {
          target: 'verifying',
          actions: assign({ operation: 'VERIFY', error: null }),
        },
        EXPORT: {
          target: 'exporting',
          actions: assign({ operation: 'EXPORT', error: null }),
        },
      },
    },
    generating: {
      on: {
        GENERATED: { target: 'generated', actions: assign({ error: null }) },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    verifying: {
      on: {
        VERIFIED: [
          {
            guard: 'verificationMatches',
            target: 'verified',
            actions: assign({ verification: 'VERIFIED', error: null }),
          },
          {
            target: 'mismatch',
            actions: assign({ verification: 'MISMATCH', error: null }),
          },
        ],
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    exporting: {
      on: {
        EXPORTED: {
          target: 'exported',
          actions: assign({ exported: true, error: null }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
        FORBIDDEN: {
          target: 'forbidden',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    generated: {
      on: { ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' } },
    },
    verified: {
      on: { ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' } },
    },
    mismatch: {
      on: { ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' } },
    },
    exported: {
      on: { ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' } },
    },
    failed: {
      on: {
        RETRY: [
          { guard: 'wasGenerating', target: 'generating', actions: assign({ error: null }) },
          { guard: 'wasVerifying', target: 'verifying', actions: assign({ error: null }) },
          { guard: 'wasExporting', target: 'exporting', actions: assign({ error: null }) },
        ],
        ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' },
      },
    },
    forbidden: {
      on: { ACKNOWLEDGE: { target: 'ready', actions: 'clearOperation' } },
    },
  },
});

export const outboxWorkerMachine = setup({
  types: {
    context: {} as {
      status: 'PENDING' | 'PROCESSING' | 'PUBLISHED' | 'RETRY' | 'DEAD_LETTER';
      attempts: number;
      canRequeue: boolean;
      error: string | null;
    },
    events: {} as
      | {
          type: 'HYDRATE';
          status: 'PENDING' | 'PROCESSING' | 'PUBLISHED' | 'RETRY' | 'DEAD_LETTER';
          attempts: number;
          canRequeue: boolean;
        }
      | { type: 'REQUEUE' }
      | { type: 'REQUEUED'; attempts: number }
      | { type: 'FAILED'; message: string }
      | { type: 'FORBIDDEN'; message: string }
      | { type: 'CONFLICT'; message: string; currentAttempts: number | null }
      | { type: 'RETRY' }
      | { type: 'ACKNOWLEDGE' },
  },
  guards: {
    isAvailable: ({ event }) => event.type === 'HYDRATE' && event.status === 'PENDING',
    isClaimed: ({ event }) => event.type === 'HYDRATE' && event.status === 'PROCESSING',
    isPublished: ({ event }) => event.type === 'HYDRATE' && event.status === 'PUBLISHED',
    isRetryable: ({ event }) => event.type === 'HYDRATE' && event.status === 'RETRY',
    isRequeueableDeadLetter: ({ event }) =>
      event.type === 'HYDRATE' && event.status === 'DEAD_LETTER' && event.canRequeue,
    canRequeue: ({ context }) => context.status === 'DEAD_LETTER' && context.canRequeue,
  },
  actions: {
    hydrate: assign({
      status: ({ event }) => (event.type === 'HYDRATE' ? event.status : 'PENDING'),
      attempts: ({ event }) => (event.type === 'HYDRATE' ? event.attempts : 0),
      canRequeue: ({ event }) => event.type === 'HYDRATE' && event.canRequeue,
      error: null,
    }),
    requeued: assign({
      status: 'RETRY',
      attempts: ({ event, context }) =>
        event.type === 'REQUEUED' ? event.attempts : context.attempts,
      canRequeue: false,
      error: null,
    }),
    failed: assign({
      error: ({ event }) => (event.type === 'FAILED' ? event.message : null),
    }),
    forbidden: assign({
      error: ({ event }) => (event.type === 'FORBIDDEN' ? event.message : null),
    }),
    conflicted: assign({
      attempts: ({ event, context }) =>
        event.type === 'CONFLICT' && event.currentAttempts !== null
          ? event.currentAttempts
          : context.attempts,
      error: ({ event }) => (event.type === 'CONFLICT' ? event.message : null),
    }),
    clearError: assign({ error: null }),
  },
}).createMachine({
  id: 'outboxWorker',
  initial: 'available',
  context: { status: 'PENDING', attempts: 0, canRequeue: false, error: null },
  on: {
    HYDRATE: [
      { guard: 'isAvailable', target: '.available', actions: 'hydrate' },
      { guard: 'isClaimed', target: '.claimed', actions: 'hydrate' },
      { guard: 'isPublished', target: '.published', actions: 'hydrate' },
      { guard: 'isRetryable', target: '.retryable', actions: 'hydrate' },
      { guard: 'isRequeueableDeadLetter', target: '.deadLetter', actions: 'hydrate' },
      { target: '.terminal', actions: 'hydrate' },
    ],
  },
  states: {
    available: {},
    claimed: {},
    published: {},
    retryable: {},
    deadLetter: {
      on: { REQUEUE: { guard: 'canRequeue', target: 'requeueing', actions: 'clearError' } },
    },
    terminal: {},
    requeueing: {
      on: {
        REQUEUED: { target: 'requeued', actions: 'requeued' },
        FAILED: { target: 'failed', actions: 'failed' },
        FORBIDDEN: { target: 'forbidden', actions: 'forbidden' },
        CONFLICT: { target: 'conflict', actions: 'conflicted' },
      },
    },
    requeued: { on: { ACKNOWLEDGE: 'retryable' } },
    failed: {
      on: {
        RETRY: { guard: 'canRequeue', target: 'requeueing', actions: 'clearError' },
        ACKNOWLEDGE: { target: 'deadLetter', actions: 'clearError' },
      },
    },
    forbidden: {
      on: { ACKNOWLEDGE: { target: 'deadLetter', actions: 'clearError' } },
    },
    conflict: {
      on: { ACKNOWLEDGE: { target: 'deadLetter', actions: 'clearError' } },
    },
  },
});

export const workerJobMachine = setup({
  types: {
    context: {} as { attempts: number; error: string | null },
    events: {} as
      | { type: 'CLAIM' }
      | { type: 'SUCCEED' }
      | { type: 'FAIL'; retryable: boolean; message: string }
      | { type: 'RETRY' },
  },
  guards: {
    mayRetry: ({ context, event }) =>
      event.type === 'FAIL' && event.retryable && context.attempts < 8,
  },
}).createMachine({
  id: 'workerJob',
  initial: 'available',
  context: { attempts: 0, error: null },
  states: {
    available: { on: { CLAIM: 'processing' } },
    processing: {
      entry: assign({ attempts: ({ context }) => context.attempts + 1 }),
      on: {
        SUCCEED: 'completed',
        FAIL: [
          {
            guard: 'mayRetry',
            target: 'backoff',
            actions: assign({ error: ({ event }) => event.message }),
          },
          { target: 'deadLetter', actions: assign({ error: ({ event }) => event.message }) },
        ],
      },
    },
    backoff: { on: { RETRY: 'available' } },
    completed: { type: 'final' },
    deadLetter: { type: 'final' },
  },
});

type GovernanceEditorEvent =
  | { type: 'HYDRATE'; revision: number }
  | { type: 'CREATE' }
  | { type: 'UPDATE' }
  | { type: 'TOGGLE' }
  | { type: 'SUCCEEDED'; revision: number }
  | { type: 'CONFLICT'; serverRevision: number }
  | { type: 'FAILED'; message: string }
  | { type: 'EDIT' }
  | { type: 'RETRY' };

const createGovernanceEditorMachine = (
  id: 'academySettings' | 'retentionPolicy',
  allowedOperations: readonly ('CREATE' | 'UPDATE' | 'TOGGLE')[],
) =>
  setup({
    types: {
      context: {} as {
        revision: number;
        operation: 'CREATE' | 'UPDATE' | 'TOGGLE' | null;
        serverRevision: number | null;
        error: string | null;
      },
      events: {} as GovernanceEditorEvent,
    },
    guards: {
      mayCreate: () => allowedOperations.includes('CREATE'),
      mayUpdate: () => allowedOperations.includes('UPDATE'),
      mayToggle: () => allowedOperations.includes('TOGGLE'),
    },
  }).createMachine({
    id,
    initial: 'editing',
    context: { revision: 0, operation: null, serverRevision: null, error: null },
    states: {
      editing: {
        on: {
          HYDRATE: {
            actions: assign({
              revision: ({ event }) => event.revision,
              serverRevision: null,
              error: null,
            }),
          },
          CREATE: {
            guard: 'mayCreate',
            target: 'saving',
            actions: assign({ operation: 'CREATE', error: null }),
          },
          UPDATE: {
            guard: 'mayUpdate',
            target: 'saving',
            actions: assign({ operation: 'UPDATE', error: null }),
          },
          TOGGLE: {
            guard: 'mayToggle',
            target: 'saving',
            actions: assign({ operation: 'TOGGLE', error: null }),
          },
        },
      },
      saving: {
        on: {
          SUCCEEDED: {
            target: 'saved',
            actions: assign({
              revision: ({ event }) => event.revision,
              serverRevision: null,
              error: null,
            }),
          },
          CONFLICT: {
            target: 'conflict',
            actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
          },
          FAILED: {
            target: 'failed',
            actions: assign({ error: ({ event }) => event.message }),
          },
        },
      },
      saved: { on: { EDIT: 'editing' } },
      conflict: { on: { EDIT: 'editing' } },
      failed: { on: { RETRY: 'saving', EDIT: 'editing' } },
    },
  });

export const academySettingsMachine = createGovernanceEditorMachine('academySettings', ['UPDATE']);
export const retentionPolicyMachine = createGovernanceEditorMachine('retentionPolicy', [
  'CREATE',
  'UPDATE',
  'TOGGLE',
]);

export const academyPolicyMachine = setup({
  types: {
    context: {} as {
      status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
      revision: number;
      operation: 'CREATE' | 'PUBLISH' | 'RETIRE' | null;
      serverRevision: number | null;
      error: string | null;
    },
    events: {} as
      | {
          type: 'HYDRATE';
          status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
          revision: number;
        }
      | { type: 'CREATE' }
      | { type: 'PUBLISH' }
      | { type: 'RETIRE' }
      | {
          type: 'SUCCEEDED';
          status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
          revision: number;
        }
      | { type: 'CONFLICT'; serverRevision: number }
      | { type: 'FAILED'; message: string }
      | { type: 'EDIT' }
      | { type: 'RETRY' },
  },
  guards: {
    isDraft: ({ context }) => context.status === 'DRAFT',
    isPublished: ({ context }) => context.status === 'PUBLISHED',
  },
}).createMachine({
  id: 'academyPolicy',
  initial: 'ready',
  context: {
    status: 'DRAFT',
    revision: 0,
    operation: null,
    serverRevision: null,
    error: null,
  },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            serverRevision: null,
            error: null,
          }),
        },
        CREATE: { target: 'saving', actions: assign({ operation: 'CREATE' }) },
        PUBLISH: {
          guard: 'isDraft',
          target: 'saving',
          actions: assign({ operation: 'PUBLISH' }),
        },
        RETIRE: {
          guard: 'isPublished',
          target: 'saving',
          actions: assign({ operation: 'RETIRE' }),
        },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'saved',
          actions: assign({
            status: ({ event }) => event.status,
            revision: ({ event }) => event.revision,
            serverRevision: null,
            error: null,
          }),
        },
        CONFLICT: {
          target: 'conflict',
          actions: assign({ serverRevision: ({ event }) => event.serverRevision }),
        },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: { on: { EDIT: 'ready' } },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { RETRY: 'saving', EDIT: 'ready' } },
  },
});

export const lifecycleRuleMachine = setup({
  types: {
    context: {} as {
      active: boolean;
      revision: number;
      error: string | null;
    },
    events: {} as
      | { type: 'HYDRATE'; active: boolean; revision: number }
      | { type: 'ACTIVATE' }
      | { type: 'RETIRE' }
      | { type: 'SUCCEEDED'; active: boolean; revision: number }
      | { type: 'CONFLICT'; serverRevision: number }
      | { type: 'FAILED'; message: string }
      | { type: 'EDIT' }
      | { type: 'RETRY' },
  },
  guards: {
    isInactive: ({ context }) => !context.active,
    isActive: ({ context }) => context.active,
  },
}).createMachine({
  id: 'lifecycleRule',
  initial: 'ready',
  context: { active: false, revision: 0, error: null },
  states: {
    ready: {
      on: {
        HYDRATE: {
          actions: assign({
            active: ({ event }) => event.active,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        ACTIVATE: { guard: 'isInactive', target: 'saving' },
        RETIRE: { guard: 'isActive', target: 'saving' },
      },
    },
    saving: {
      on: {
        SUCCEEDED: {
          target: 'saved',
          actions: assign({
            active: ({ event }) => event.active,
            revision: ({ event }) => event.revision,
            error: null,
          }),
        },
        CONFLICT: { target: 'conflict' },
        FAILED: {
          target: 'failed',
          actions: assign({ error: ({ event }) => event.message }),
        },
      },
    },
    saved: { on: { EDIT: 'ready' } },
    conflict: { on: { EDIT: 'ready' } },
    failed: { on: { RETRY: 'saving', EDIT: 'ready' } },
  },
});

export const machineCatalog = [
  'application',
  'query',
  'academySettings',
  'retentionPolicy',
  'academyPolicy',
  'lifecycleRule',
  'catalogEntry',
  'curriculumPublication',
  'planAuthoring',
  'relationshipEditor',
  'taskDependency',
  'outcomeLibrary',
  'learningResource',
  'resourceVersion',
  'ratingScale',
  'rubricBuilder',
  'studyCycle',
  'room',
  'classGroup',
  'personLifecycle',
  'teacherAssignment',
  'enrollmentMovement',
  'invitationOnboarding',
  'invitationTokenDelivery',
  'passwordAction',
  'accountLifecycle',
  'roleAssignment',
  'sessionRevocation',
  'accountToken',
  'externalIdentity',
  'mfaEnrollment',
  'mfaRecovery',
  'projectionQuery',
  'guardianVerification',
  'guardianAuthority',
  'consentDecision',
  'legalHold',
  'privacyRequest',
  'messaging',
  'chatGroup',
  'conversationMembership',
  'messageSend',
  'messageReaction',
  'moderationCase',
  'assetProcessing',
  'linkPreview',
  'messageReceipt',
  'accountBlock',
  'safetyReport',
  'presentationTemplate',
  'layoutEditor',
  'interactionAuthoring',
  'presentationEditor',
  'delivery',
  'selfPacedProgress',
  'meeting',
  'meetingAudio',
  'meetingSynchronization',
  'interactionResponse',
  'recording',
  'recordingOutput',
  'scheduleMaterialization',
  'classSession',
  'groupTask',
  'taskAttempt',
  'attemptGrading',
  'attendance',
  'skillProjection',
  'enrollmentOutcome',
  'auditExplorer',
  'ingestKey',
  'integrityCheckpoint',
  'outboxWorker',
  'workerJob',
] as const;
